import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../supabase";
import { Btn, Checkbox, EmptyState, FileInput, FilterPill, Input, PageHeader, Select, TextLink, DataTable, TabBar, Switch, Textarea } from "../ui";
import { validateUploadFile, safeNum, formatCurrency, escapeFilterValue, normalizeEmail, formatPersonName, parseNameParts, formatPhoneInput, parseLocalDate, formatLocalDate, emailFilterValue, sameAddress, propertyLabel, fmtDate, fmtDateTime } from "../utils/helpers";
import { pmError } from "../utils/errors";
import { guardSubmit, guardRelease } from "../utils/guards";
import { logAudit } from "../utils/audit";
import { runDataIntegrityChecks, saveCompanySettings, loadCompanySettings } from "../utils/company";
import { queueNotification } from "../utils/notifications";
import { COMPANY_DEFAULTS } from "../config";
import { fetchAllPaged } from "../utils/accounting";
import { loadOpenWork, dismissOpenItem, waivePropertyRecord, waiveTenantDoc as waiveTenantDoc_, defaultApprover, TASK_KINDS, PROPERTY_RECORD_LABELS, withReturn, TASKS_RETURN } from "../utils/openWork";
import { TASKS_VIEWS, TASK_DOC_LABEL, toEntries, urgencyBuckets, groupByProperty, railEntries, entryRailKey, isOverdueEntry, chipLabel } from "../utils/tasksViews";
import { loadAccountPref, saveAccountPref } from "../utils/userPrefs";
import { approvePropertyRequest, rejectPropertyRequest } from "../utils/approvals";
import { clockAction, CLOCK_KINDS } from "../utils/leaseClockRules";
import { Spinner, Modal } from "./shared";
import AdminNotificationRules from "./AdminNotificationRules";
import { MfaPanel } from "./SecondStep";
import { resetMemberMfa } from "../utils/mfa";
import { withStepUp } from "../utils/stepUp";
import { clearCapabilityCache } from "../utils/capabilities";
import { inviteChip, inviteConfirmText, inviteResultText } from "../utils/invitations";
import { EXTRA_CAPS, capLabel, personCapabilities, roleCapabilities, cardPages, prefillCard, togglePageOnCard, lockedOnCard, notVisibleYet, savingTakesAway, memberGrantRows } from "../utils/accessRules";

// ============ ROLE DEFINITIONS ============
// Mirror of ROLES in App.js. The two must stay in sync — App.js is
// the source of truth for page access, Admin.js uses this to render
// the role legend, the role dropdown, and the "customizable modules"
// UI. The older in-file copy diverged over time (missing manager,
// tax_bills, messages, latefees); keeping one canonical shape here
// prevents that from silently happening again.
const ROLES = {
  admin: { label: "Admin", color: "bg-brand-600", pages: ["dashboard","tasks","properties","tenants","prospects","payments","maintenance","utilities","hoa","loans","insurance","tax_bills","licenses","accounting","owners","notifications","messages","admin","documents","doc_builder","leases","autopay","inspections","vendors","moveout","evictions","latefees"] },
  manager: { label: "Manager", color: "bg-brand-400", pages: ["dashboard","tasks","properties","tenants","prospects","payments","maintenance","utilities","hoa","tax_bills","licenses","accounting","notifications","messages","documents","doc_builder","leases","inspections","vendors","moveout","evictions"] },
  office_assistant: { label: "Office Assistant", color: "bg-info-500", pages: ["dashboard","tasks","properties","tenants","prospects","payments","maintenance","utilities","hoa","tax_bills","licenses","accounting","notifications","messages","admin","documents","doc_builder","leases","inspections","vendors","moveout","evictions"] },
  accountant: { label: "Accountant", color: "bg-positive-600", pages: ["dashboard","accounting","payments","utilities"] },
  maintenance: { label: "Maintenance", color: "bg-notice-500", pages: ["maintenance","vendors"] },
  tenant: { label: "Tenant", color: "bg-brand-50/300", pages: ["tenant_portal"] },
  owner: { label: "Owner", color: "bg-highlight-600", pages: ["owner_portal","loans"] },
};

const ALL_NAV = [
  { id: "dashboard", label: "Dashboard", icon: "dashboard" },
  { id: "tasks", label: "Tasks & Approvals", icon: "assignment" },
  { id: "properties", label: "Properties", icon: "apartment", children: [
    { id: "maintenance", label: "Maintenance", icon: "build" },
    { id: "inspections", label: "Inspections", icon: "checklist" },
    { id: "utilities", label: "Utilities", icon: "bolt" },
    { id: "hoa", label: "HOA Payments", icon: "holiday_village" },
    { id: "loans", label: "Loans", icon: "account_balance_wallet" },
    { id: "insurance", label: "Insurance", icon: "verified_user" },
    { id: "tax_bills", label: "Tax Bills", icon: "receipt_long" },
    { id: "licenses", label: "Licenses", icon: "verified" },
  ]},
  { id: "tenants", label: "Tenants", icon: "people" },
  { id: "prospects", label: "Prospects", icon: "person_search" },
  { id: "payments", label: "Payments", icon: "payments" },
  // Its sub-pages are on the card too, so "Accounting yes, bank no" is said
  // in one place. Mirrors App.js ALL_NAV (impliesChildren: the menu shows a
  // ticked Accounting's sub-pages, less those whose data is withheld).
  { id: "accounting", label: "Accounting", icon: "account_balance", impliesChildren: true, children: [
    { id: "acct_opening",    label: "Opening Balances",  icon: "restart_alt" },
    { id: "acct_coa",        label: "Chart of Accounts", icon: "list_alt" },
    { id: "acct_journal",    label: "Journal Entries",   icon: "receipt_long" },
    { id: "acct_recurring",  label: "Recurring Entries", icon: "autorenew" },
    { id: "acct_bankimport", label: "Bank Transactions", icon: "credit_card" },
    { id: "acct_qbimport",   label: "Import from QuickBooks", icon: "cloud_upload" },
    { id: "acct_reconcile",  label: "Reconcile",         icon: "account_balance_wallet" },
    { id: "acct_classes",    label: "Class Tracking",    icon: "category" },
    { id: "acct_reports",    label: "Reports",           icon: "assessment" },
  ]},
  { id: "doc_builder", label: "Document Builder", icon: "description" },
  { id: "vendors", label: "Vendors", icon: "engineering" },
  { id: "owners", label: "Owners", icon: "person" },
  { id: "messages", label: "Messages", icon: "forum" },
  { id: "notifications", label: "Notifications", icon: "notifications_active" },
];
// Flat list of all nav IDs including children (for settings UI and allowedPages)
const ALL_NAV_FLAT = ALL_NAV.flatMap(n => n.children ? [n, ...n.children] : [n]);
// Child page IDs that live under a parent in sidebar
const NAV_CHILD_IDS = new Set(ALL_NAV.flatMap(n => (n.children || []).map(c => c.id)));


// ============ REUSABLE ARCHIVED ITEMS COMPONENT ============
// A work order with accounting entries or vendor invoices cannot be
// permanently deleted (DB trigger + FK RESTRICT): deleting it used to unlink
// its invoice, so paying the invoice expensed the repair a second time.
// Same for a vendor with invoices: deleting it used to cascade its invoices
// away, so a paid repair could be expensed again.
function bookedDeleteMessage(table, error) {
  if (!error) return null;
  if (table === "work_orders" && (error.hint === "work_order_booked" || error.code === "23503")) {
    return "This work order has accounting entries or vendor invoices, so it can't be permanently deleted. It stays archived; its history is kept for the books.";
  }
  if (table === "vendors" && (error.hint === "vendor_has_invoices" || error.code === "23503")) {
    return "This vendor has invoices, so it can't be permanently deleted. It stays archived; its invoices are kept for the books.";
  }
  return null;
}

function ArchivedItems({ tableName, label, fields, companyId, addNotification, onRestore, showConfirm, userProfile, userRole }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchItems(); }, [companyId]);

  async function fetchItems() {
  setLoading(true);
  const { data } = await supabase.from(tableName).select(fields).eq("company_id", companyId).not("archived_at", "is", null).order("archived_at", { ascending: false }).limit(200);
  setItems(data || []);
  setLoading(false);
  }

  async function restore(item) {
  if (!await showConfirm({ message: "Restore this " + label.toLowerCase() + "?" })) return;
  const { error } = await supabase.from(tableName).update({ archived_at: null, archived_by: null }).eq("id", item.id).eq("company_id", companyId);
  if (error) { pmError("PM-8006", { raw: error, context: "restoring archived " + label.toLowerCase() }); return; }
  addNotification("♻️", "Restored " + label + ": " + (item.address || item.name || item.issue || item.tenant || "item"));
  fetchItems();
  if (onRestore) onRestore();
  }

  async function permanentDelete(item) {
  if (!await showConfirm({ message: "PERMANENTLY delete this " + label.toLowerCase() + "? This cannot be undone.", variant: "danger", confirmText: "Delete" })) return;
  const { error } = await supabase.from(tableName).delete().eq("id", item.id).eq("company_id", companyId);
  const booked = bookedDeleteMessage(tableName, error);
  if (booked) { addNotification("⚠️", booked); return; }
  if (error) { pmError("PM-8006", { raw: error, context: "permanently deleting " + label.toLowerCase() }); return; }
  logAudit("delete", tableName, "Permanently deleted " + label + ": " + (item.name || item.address || item.id), item.id, userProfile?.email, userRole, companyId);
  addNotification("🗑️", "Deleted " + label);
  fetchItems();
  }

  if (loading) return <Spinner />;

  return (
  <div>
  {items.length === 0 ? (
  <div className="text-center py-12 bg-white rounded-xl border border-neutral-200"><div className="text-subtle-400">No archived {label.toLowerCase()}s</div></div>
  ) : (
  <div className="space-y-2">
  {items.map(item => (
  <div key={item.id} className="bg-white rounded-xl border border-neutral-200 p-4 flex items-center gap-4 opacity-70">
  <div className="flex-1">
  <div className="font-semibold text-subtle-700 text-sm">{item.address || item.name || item.issue || item.tenant || "Item"}</div>
  <div className="text-xs text-subtle-400">
  {item.property && <span>{item.property} · </span>}
  {item.amount && <span>${Number(item.amount).toLocaleString()} · </span>}
  Archived {fmtDate(item.archived_at)}
  {item.archived_by && <span> by {item.archived_by}</span>}
  </div>
  <div className="text-xs text-warn-600 mt-1">{item.archived_at ? Math.max(0, 180 - Math.floor((Date.now() - new Date(item.archived_at)) / 86400000)) : "?"} days until auto-purge</div>
  </div>
  <Btn variant="success" size="sm" onClick={() => restore(item)}>♻️ Restore</Btn>
  <Btn variant="danger" size="sm" onClick={() => permanentDelete(item)}>🗑️ Delete</Btn>
  </div>
  ))}
  </div>
  )}
  </div>
  );
}


// ============ ROLE MANAGEMENT ============
function RoleManagement({ addNotification, companyId, showToast, showConfirm, userProfile }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // user being edited
  const formRef = useRef(null);
  // Bring the opened form into view: under the header for Add, inside the
  // person's card for Edit.
  useEffect(() => {
  if (showForm) formRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [showForm, editingUser]);
  const [form, setForm] = useState({ email: "", role: "office_assistant", name: "", first_name: "", mi: "", last_name: "", manager_email: "" });
  // customPages: which modules are toggled ON when adding/editing a user
  const [customPages, setCustomPages] = useState([]);
  // Which invites are in flight, keyed the same way as the submit guard.
  const [inviting, setInviting] = useState({});
  // Staff invitations (2026-10-20): who is already active here, and the
  // company's name for the confirmation.
  const [activeEmails, setActiveEmails] = useState(() => new Set());
  const [companyName, setCompanyName] = useState("");
  // The card is the one place for access (Sahil, 2026-10-19): its pages
  // grant the data they need, and "Also allowed to" holds the rest. perm:
  // company_permissions rows + role_capability_defaults(); cardExtras: the
  // "Also allowed to" ticks; cardCaps: what the person holds today (null
  // for someone new), for the "Not visible yet" notes.
  const [perm, setPerm] = useState({ rows: [], defaults: null, error: "" });
  const [cardExtras, setCardExtras] = useState({});
  const [cardCaps, setCardCaps] = useState(null);

  // All modules that can be assigned (admin and tenant are fixed, not customizable)
  const CUSTOMIZABLE_ROLES = ["office_assistant", "accountant", "maintenance", "manager"];

  // Candidate reviewers for the "Manager" dropdown. Includes admins and
  // any existing manager — they're the only roles that can approve.
  const managerCandidates = users.filter(u => u.role === "admin" || u.role === "manager");

  useEffect(() => { fetchUsers(); }, [companyId]);

  async function fetchUsers() {
  const [{ data }, { data: mems }, permRes, defRes, { data: co }] = await Promise.all([
    supabase.from("app_users").select("*").eq("company_id", companyId).is("archived_at", null).order("name"),
    supabase.from("company_members").select("user_email, status, role, invited_at, expires_at, declined_at, created_at").eq("company_id", companyId),
    supabase.from("company_permissions").select("subject, capability, allowed").eq("company_id", companyId),
    supabase.rpc("role_capability_defaults"),
    supabase.from("companies").select("name").eq("id", companyId).maybeSingle(),
  ]);
  const memMap = Object.fromEntries((mems || []).map(m => [m.user_email?.toLowerCase(), m]));
  // Everyone already active here, for "Already in this company" on the form.
  setActiveEmails(new Set((mems || []).filter(m => m.status === "active").map(m => normalizeEmail(m.user_email))));
  setCompanyName(co?.name || "");
  setUsers((data || []).map(u => { const m = memMap[u.email?.toLowerCase()] || null; return { ...u, _member: m, _memberStatus: m?.status || null, _memberRole: m?.role || null }; }));
  const permErr = permRes.error || defRes.error;
  if (permErr) pmError("PM-8001", { raw: permErr, context: "load team permissions", silent: true });
  setPerm({ rows: permRes.data || [], defaults: defRes.data || null, error: permErr ? permErr.message : "" });
  setLoading(false);
  }

  // What a role starts a card with: its pages, less any page whose data the
  // role does not hold, and its "Also allowed to" (rule 3).
  function prefillFromRole(role) {
  const { pages, extras } = prefillCard(ROLES[role]?.pages || [], roleCapabilities(role, perm.rows, perm.defaults), ALL_NAV);
  setCustomPages(pages);
  setCardExtras(extras);
  }

  // When role changes in the form, pre-fill the card from that role
  function handleRoleChange(role) {
  setForm(f => ({ ...f, role }));
  prefillFromRole(role);
  }

  function togglePage(pageId) {
  setCustomPages(prev => togglePageOnCard(ALL_NAV, prev, pageId));
  }

  function startAdd() {
  setEditingUser(null);
  setForm({ email: "", role: "office_assistant", name: "", first_name: "", mi: "", last_name: "", manager_email: "" });
  setCardCaps(null);
  prefillFromRole("office_assistant");
  setShowForm(true);
  }

  function startEdit(u) {
  setEditingUser(u);
  const parsed = parseNameParts(u.name);
  setForm({ email: u.email, role: u.role, name: u.name, first_name: u.first_name || parsed.first_name, mi: u.middle_initial || parsed.middle_initial, last_name: u.last_name || parsed.last_name, manager_email: u.manager_email || "" });
  // What they hold today, as the database answers it.
  const caps = personCapabilities(u._memberRole || u.role, u.email, perm.rows, perm.defaults);
  setCardCaps(caps);
  const extras = {};
  for (const c of EXTRA_CAPS) extras[c.id] = !!caps[c.id];
  setCardExtras(extras);
  // Their saved pages as the menu reads them; with none saved, what their
  // role shows them today (role pages they can see).
  if (u.custom_pages) {
    let saved = [];
    try { saved = JSON.parse(u.custom_pages) || []; } catch { saved = ROLES[u.role]?.pages || []; }
    setCustomPages(cardPages(saved, ALL_NAV));
  } else {
    setCustomPages(prefillCard(ROLES[u.role]?.pages || [], caps, ALL_NAV).pages);
  }
  setShowForm(true);
  }

  // Rule 1 + 2: every capability, explicitly, for this person. Only an
  // admin can write these (company_permissions row security).
  async function saveGrants(email, oldEmail) {
  const rows = memberGrantRows(companyId, email, customPages, cardExtras);
  const { error } = await supabase.from("company_permissions").upsert(rows, { onConflict: "company_id,subject,capability" });
  if (error) return error;
  if (oldEmail && normalizeEmail(oldEmail) !== normalizeEmail(email)) {
    await supabase.from("company_permissions").delete().eq("company_id", companyId).eq("subject", "member:" + normalizeEmail(oldEmail));
  }
  // Their session picks the change up on its next load.
  clearCapabilityCache();
  const on = rows.filter(r => r.allowed).map(r => capLabel(r.capability));
  logAudit("update", "team", `Access for ${normalizeEmail(email)}: ${on.length ? on.join(", ") : "no data permissions"}`, normalizeEmail(email), userProfile?.email, "admin", companyId);
  return null;
  }

  async function saveUser() {
  if (!guardSubmit("saveUser")) return;
  try {
  // These mirror, field for field, what the Save button's `disabled`
  // expression used to swallow. The button is now always clickable so
  // the user gets a message naming the actual problem instead of a
  // greyed-out button with no explanation.
  if (!form.first_name.trim()) { showToast("First name is required.", "error"); return; }
  if (!form.last_name.trim()) { showToast("Last name is required.", "error"); return; }
  if (!form.email.trim()) { showToast("Email is required.", "error"); return; }
  if (!form.email.includes("@")) { showToast("Please enter a valid email address.", "error"); return; }
  if (customPages.length === 0) { showToast("Please select at least one module.", "error"); return; }
  const writesGrants = CUSTOMIZABLE_ROLES.includes(form.role);
  if (writesGrants && (perm.error || !perm.defaults)) { showToast("Permissions could not be loaded, so access was not saved. Reload the page and try again." + (perm.error ? " (" + perm.error + ")" : ""), "error"); return; }

  // Admin/tenant never get a manager — admin sits at the top of the
  // approval chain, and tenants don't submit staff requests.
  const managerEmail = (form.role === "admin" || form.role === "tenant") ? null : (form.manager_email || null);
  const payload = {
  email: form.email,
  role: form.role,
  name: form.name,
  first_name: form.first_name,
  middle_initial: form.mi,
  last_name: form.last_name,
  manager_email: managerEmail,
  custom_pages: JSON.stringify(customPages),
  company_id: companyId,
  };

  if (editingUser) {
  const emailChanged = editingUser.email && normalizeEmail(editingUser.email) !== normalizeEmail(payload.email);
  if (emailChanged) {
  // Atomic email change: delete old membership + update user + create new membership in one transaction
  try {
  const { error: rpcErr } = await supabase.rpc("change_user_email", {
  p_company_id: companyId,
  p_user_id: String(editingUser.id),
  p_old_email: editingUser.email,
  p_new_email: payload.email,
  p_name: payload.name,
  p_role: payload.role,
  p_custom_pages: JSON.stringify(customPages),
  });
  if (rpcErr) throw new Error(rpcErr.message);
  // RPC signature doesn't accept manager_email — tack it on after.
  await supabase.from("app_users").update({ manager_email: managerEmail })
    .eq("company_id", companyId).eq("id", editingUser.id);
  await supabase.from("company_members").update({ manager_email: managerEmail })
    .eq("company_id", companyId).ilike("user_email", emailFilterValue(payload.email));
  } catch (rpcE) {
  pmError("PM-1009", { raw: rpcE, context: "update user email via RPC" });
  return;
  }
  } else {
  // No email change — just update role/name/pages + manager
  const { error } = await supabase.from("app_users").update({ email: normalizeEmail(payload.email), role: payload.role, name: payload.name, manager_email: managerEmail, custom_pages: payload.custom_pages, company_id: payload.company_id }).eq("company_id", companyId).eq("id", editingUser.id);
  if (error) { pmError("PM-8006", { raw: error, context: "save reconciliation" }); return; }
  // The membership carries the role and pages the app actually uses. Its
  // status is left alone: editing an invited or pending person used to
  // activate them, and a failed write still said "access updated" (FE-19).
  const { data: memRows, error: memErr } = await supabase.from("company_members").update({ user_name: form.name, role: form.role, manager_email: managerEmail, custom_pages: JSON.stringify(customPages) })
    .eq("company_id", companyId).ilike("user_email", emailFilterValue(form.email || "")).select("id");
  if (memErr || !memRows?.length) {
    // put app_users back so the two cannot disagree
    await supabase.from("app_users").update({ role: editingUser.role, name: editingUser.name, manager_email: editingUser.manager_email || null, custom_pages: editingUser.custom_pages || null }).eq("company_id", companyId).eq("id", editingUser.id);
    showToast("Access NOT updated: " + (memErr?.message || "no membership found for " + form.email), "error");
    return;
  }
  }
  if (writesGrants) {
    const gErr = await saveGrants(payload.email, editingUser.email);
    if (gErr) { pmError("PM-8006", { raw: gErr, context: "save data permissions" }); showToast("Pages saved, but data permissions were NOT: " + gErr.message, "error"); fetchUsers(); return; }
  }
  addNotification("👥", `${form.name}'s access updated`);
  } else {
  // A new person is an INVITATION (owner decision, 2026-10-20): no access
  // until they accept. Someone already active here is told so up front.
  const newEmail = normalizeEmail(payload.email);
  if (activeEmails.has(newEmail)) { showToast("Already in this company — edit their card instead.", "error"); return; }
  const roleLabel = ROLES[form.role]?.label || form.role;
  const check = await inviteRequest({ action: "check", email: newEmail, companyId });
  const hasAccount = check.ok ? !!check.json.has_account : null;
  if (check.ok && check.json.member_status === "active") { showToast("Already in this company — edit their card instead.", "error"); return; }
  if (!await showConfirm({ message: inviteConfirmText({ name: form.name, email: newEmail, companyName, roleLabel, hasAccount }), confirmText: "Send invitation" })) return;
  // The card's row. One left from an earlier removal is brought back rather
  // than duplicated ((email, company_id) is unique).
  const { data: prior } = await supabase.from("app_users").select("id").eq("company_id", companyId).ilike("email", emailFilterValue(newEmail)).limit(1);
  const cardRow = { ...payload, email: newEmail, archived_at: null, archived_by: null };
  const { error } = prior?.[0]
    ? await supabase.from("app_users").update(cardRow).eq("company_id", companyId).eq("id", prior[0].id)
    : await supabase.from("app_users").insert([cardRow]);
  if (error) { pmError("PM-8006", { raw: error, context: "save team member card" }); return; }
  if (writesGrants) {
    const gErr = await saveGrants(payload.email, null);
    if (gErr) { pmError("PM-8006", { raw: gErr, context: "save data permissions" }); showToast(form.name + "'s card was saved, but data permissions were NOT: " + gErr.message, "error"); fetchUsers(); return; }
  }
  // The invitation itself (membership 'invited' + the email) is the server's.
  await sendInvitation({ name: form.name, email: newEmail, role: form.role, customPages, managerEmail });
  }

  setShowForm(false);
  setEditingUser(null);
  setForm({ email: "", role: "office_assistant", name: "", first_name: "", mi: "", last_name: "" });
  setCustomPages([]);
  fetchUsers();
  } finally { guardRelease("saveUser"); }
  }

  async function removeUser(id, name, email) {
  if (!guardSubmit("removeUser")) return;
  try {
  if (!await showConfirm({ message: `Remove ${name}?`, variant: "danger", confirmText: "Delete" })) return;
  await supabase.from("app_users").update({ archived_at: new Date().toISOString(), archived_by: userProfile?.email }).eq("id", id).eq("company_id", companyId);
  // Also deactivate their company membership
  if (email) {
  const { error: _err7920 } = await supabase.from("company_members").update({ status: "removed" }).eq("company_id", companyId).eq("user_email", email.toLowerCase());
  if (_err7920) { showToast("Error updating company_members: " + _err7920.message, "error"); return; }
  }
  addNotification("👥", `${name} removed`);
  fetchUsers();
  } finally { guardRelease("removeUser"); }
  }

  // POST /api/invite-user with the admin's session. Routed server-side: the
  // service role writes the invitation and its token and sends the email,
  // and Supabase's Bot Protection captcha is not in the way. A TIMEOUT,
  // because a hung request would otherwise hold the submit guard for the
  // life of the page (the "button does nothing" report).
  async function inviteRequest(body) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return { ok: false, status: 401, json: { error: "Session expired — please sign in again." } };
    const resp = await fetch("/api/invite-user", {
      method: "POST",
      signal: AbortSignal.timeout(30000),
      headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
      body: JSON.stringify(body),
    });
    let json = {};
    try { json = await resp.json(); } catch (_) {}
    return { ok: resp.ok, status: resp.status, json };
  } catch (e) {
    return { ok: false, status: 0, json: { error: e.message } };
  }
  }

  // Send (or resend) an invitation and say what happened, in one line.
  // "Invite sent" reaches the bell and the audit trail only when an email
  // actually went out.
  async function sendInvitation({ name, email, role, customPages: pages = null, managerEmail = undefined }) {
  const roleName = ROLES[role]?.label || role;
  const body = { email, companyId, userName: name, role, inviteType: "team" };
  if (pages) body.customPages = pages;
  if (managerEmail !== undefined) body.managerEmail = managerEmail;
  const r = await inviteRequest(body);
  if (!r.ok) {
    const errMsg = r.json?.error || "Invite failed (" + r.status + ")";
    // 409 = already an active member: said, not logged as a failure.
    if (r.status === 409) { showToast(errMsg, "info"); fetchUsers(); return null; }
    pmError("PM-1007", { raw: { message: errMsg }, context: "send team invitation to " + email, silent: true });
    showToast("The invitation was not sent: " + errMsg, "error");
    fetchUsers();
    return null;
  }
  const line = inviteResultText({ name, email, result: r.json });
  if (r.json.email_sent) {
    addNotification("✉️", `Invite sent to ${name || email} (${roleName})`);
    logAudit("invite", "team", "Invited " + (name || email) + " as " + roleName + ": " + email, email, userProfile?.email, "admin", companyId);
  }
  showToast(line.text, line.tone);
  fetchUsers();
  return r.json;
  }

  // The card's Invite / Resend / Invite again.
  async function inviteUser(user) {
  // Keyed PER USER: a shared key meant inviting one teammate blocked
  // inviting a different one, indistinguishable from a dead button.
  const who = user.id || user.email || "";
  const guardKey = "inviteUser:" + who;
  if (!guardSubmit("inviteUser", who)) return;
  setInviting(prev => ({ ...prev, [guardKey]: true }));
  try {
  if (!user.email) { showToast("This user has no email address.", "error"); return; }
  const email = normalizeEmail(user.email);
  const role = user._memberRole || user.role;
  const check = await inviteRequest({ action: "check", email, companyId });
  if (check.ok && check.json.member_status === "active") { showToast("Already in this company — edit their card instead.", "info"); fetchUsers(); return; }
  const again = ["invited", "declined", "cancelled"].includes(user._memberStatus);
  const message = inviteConfirmText({ name: user.name, email, companyName, roleLabel: ROLES[role]?.label || role, hasAccount: check.ok ? !!check.json.has_account : null, again });
  if (!await showConfirm({ message, confirmText: again ? "Send new invitation" : "Send invitation" })) return;
  await sendInvitation({ name: user.name, email, role });
  } finally {
    guardRelease("inviteUser", who);
    setInviting(prev => { const n = { ...prev }; delete n[guardKey]; return n; });
  }
  }

  // Cancel an open invitation: status 'cancelled', its links stop working.
  async function cancelInvite(user) {
  const who = user.id || user.email || "";
  if (!guardSubmit("cancelInvite", who)) return;
  try {
  if (!await showConfirm({ message: `Cancel the invitation to ${user.name || user.email}?\n\nThe link in their email stops working. Their card stays, and you can invite them again later.`, variant: "danger", confirmText: "Cancel invitation", cancelText: "Keep it" })) return;
  const { error } = await supabase.rpc("cancel_company_invitation", { p_company_id: companyId, p_email: normalizeEmail(user.email) });
  if (error) { showToast("The invitation was not cancelled: " + error.message, "error"); fetchUsers(); return; }
  showToast(`Invitation to ${user.name || user.email} cancelled.`, "success");
  fetchUsers();
  } finally { guardRelease("cancelInvite", who); }
  }

  // A lost authenticator is reset by an admin (Sahil, 2026-10-23): every authenticator
  // the person has is removed and they are signed out everywhere; at their
  // next sign-in they set up a new one. The server checks that the caller is
  // an admin here and the person is an active member of this company.
  async function resetMfa(user) {
  const who = user.id || user.email || "";
  if (!guardSubmit("resetMfa", who)) return;
  try {
  if (!await showConfirm({ message: `Reset ${user.name || user.email}'s authenticator?\n\nEvery authenticator they have is removed and they are signed out. At their next sign-in they will set up a new one. Do this when they have lost or replaced their phone.`, variant: "danger", confirmText: "Reset authenticator" })) return;
  const r = await resetMemberMfa(supabase, companyId, normalizeEmail(user.email), withStepUp);
  if (!r.ok) { showToast("The authenticator was not reset: " + r.error, "error"); return; }
  showToast(r.removed ? `${user.name || user.email}'s authenticator was reset (${r.removed} removed).` : `${user.name || user.email} had no authenticator to reset.`, "success");
  } finally { guardRelease("resetMfa", who); }
  }

  // Get the effective pages for a user — custom_pages takes priority over role default
  function getEffectivePages(u) {
  if (u.custom_pages) {
  try { return JSON.parse(u.custom_pages); } catch { /* fall through */ }
  }
  return ROLES[u.role]?.pages || [];
  }

  if (loading) return <Spinner />;

  const isCustomizable = CUSTOMIZABLE_ROLES.includes(form.role);
  // Measured against what the person holds today (none for someone new).
  const notYet = isCustomizable ? notVisibleYet(customPages, cardCaps) : [];
  const takesAway = isCustomizable ? savingTakesAway(customPages, cardExtras, cardCaps) : [];

  // One form for Add and Edit. Add opens it above the list; Edit opens it
  // inside that person's card, where the Edit button was clicked (it used to
  // open at the top of the page, far from the card, 2026-10-21).
  const formPanel = showForm && (
  <div ref={formRef} className={editingUser ? "mt-4 pt-4 border-t border-neutral-100" : "bg-white rounded-xl border border-neutral-200 shadow-card p-4 mb-4"}>
  <h3 className="text-sm font-semibold text-neutral-700 mb-3">{editingUser ? `Edit — ${editingUser.name}` : "Add Team Member"}</h3>

  {/* Basic info */}
  <div className="grid grid-cols-6 gap-2 mb-3">
  <div className="col-span-2"><label className="text-2xs font-medium text-neutral-500 uppercase tracking-wider mb-1 block">First Name *</label><Input size="sm" value={form.first_name} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, first_name: v, name: formatPersonName(v, f.mi, f.last_name) })); }} placeholder="First" /></div>
  <div className="col-span-1"><label className="text-2xs font-medium text-neutral-500 uppercase tracking-wider mb-1 block">MI</label><Input size="sm" maxLength={1} value={form.mi} onChange={e => { const v = e.target.value.toUpperCase(); setForm(f => ({ ...f, mi: v, name: formatPersonName(f.first_name, v, f.last_name) })); }} placeholder="M" className="text-center" /></div>
  <div className="col-span-3"><label className="text-2xs font-medium text-neutral-500 uppercase tracking-wider mb-1 block">Last Name *</label><Input size="sm" value={form.last_name} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, last_name: v, name: formatPersonName(f.first_name, f.mi, v) })); }} placeholder="Last" /></div>
  <div className="col-span-4"><label className="text-2xs font-medium text-neutral-500 uppercase tracking-wider mb-1 block">Email *</label><Input size="sm" type="email" placeholder="Email address" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} disabled={!!editingUser} autoComplete="off" className="disabled:bg-brand-50/30 disabled:text-neutral-400" />
  {!editingUser && form.email.includes("@") && activeEmails.has(normalizeEmail(form.email)) && (() => {
    const card = users.find(x => normalizeEmail(x.email) === normalizeEmail(form.email));
    return <div className="mt-1 text-xs text-notice-700" role="alert">Already in this company — {card ? <TextLink tone="brand" size="xs" onClick={() => startEdit(card)}>edit their card</TextLink> : "edit their card"} instead.</div>;
  })()}
  </div>
  <div className="col-span-2"><label className="text-2xs font-medium text-neutral-500 uppercase tracking-wider mb-1 block">Role</label><Select size="sm" value={form.role} onChange={e => handleRoleChange(e.target.value)}>
  {Object.entries(ROLES).filter(([k]) => k !== "tenant").map(([key, r]) => (
  <option key={key} value={key}>{r.label}</option>
  ))}
  </Select></div>
  {/* Approval manager — which admin/manager reviews this user's
      property-change and document-exception requests. Hidden for
      admin (no reviewer above them) and tenant (doesn't submit). */}
  {form.role !== "admin" && form.role !== "tenant" && (
  <div className="col-span-6"><label className="text-2xs font-medium text-neutral-500 uppercase tracking-wider mb-1 block">Approval Manager <span className="text-neutral-400 normal-case">(who reviews their edit/exception requests)</span></label>
  <Select size="sm" value={form.manager_email} onChange={e => setForm({ ...form, manager_email: e.target.value })}>
  <option value="">— No manager (admin reviews) —</option>
  {managerCandidates.filter(m => m.email && m.email !== form.email).map(m => (
  <option key={m.id} value={m.email}>{m.name || m.email} ({ROLES[m.role]?.label || m.role})</option>
  ))}
  </Select></div>
  )}
  </div>

  {/* Module picker — only shown for customizable roles */}
  {isCustomizable && (
  <div className="border border-brand-100 rounded-xl p-3 bg-brand-50/20">
  <div className="flex items-center justify-between mb-2">
  <div className="text-xs font-semibold text-neutral-700">Module access <span className="ml-1 text-neutral-400 font-normal">· {customPages.filter(id => ALL_NAV_FLAT.some(n => n.id === id)).length} of {ALL_NAV_FLAT.length}</span></div>
  <div className="flex gap-2 text-xs">
  <TextLink tone="brand" size="xs" onClick={() => setCustomPages(ALL_NAV_FLAT.map(n => n.id))}>Select all</TextLink>
  <span className="text-neutral-300">|</span>
  <TextLink tone="neutral" size="xs" onClick={() => setCustomPages([])}>Clear all</TextLink>
  </div>
  </div>
  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-0.5">
  {ALL_NAV_FLAT.map(nav => {
  const isOn = customPages.includes(nav.id);
  const locked = lockedOnCard(ALL_NAV, customPages, nav.id);
  const isSub = NAV_CHILD_IDS.has(nav.id);
  const hidden = isOn && notYet.some(m => m.pages.includes(nav.id));
  return (
  <label key={nav.id} title={locked ? "Comes with Accounting" : undefined} className={`flex items-center gap-2 py-1 px-1 rounded hover:bg-brand-50 text-xs text-neutral-600 ${locked ? "cursor-default" : "cursor-pointer"} ${isSub ? "pl-4" : ""}`}>
  <Checkbox checked={isOn} disabled={locked} onChange={() => togglePage(nav.id)} className="accent-brand-600 w-3.5 h-3.5" />
  <span className="material-icons-outlined text-sm text-neutral-400">{nav.icon}</span>
  <span className={isOn ? "text-neutral-700 font-medium" : ""}>{nav.label}</span>
  {hidden && <span className="material-icons-outlined text-sm text-notice-600" aria-label="Not visible yet">error_outline</span>}
  </label>
  );
  })}
  </div>
  {/* Rule 5: a ticked page whose data this person does not hold yet. */}
  {notYet.map(m => (
  <div key={m.cap} className="mt-2 bg-notice-50 border border-notice-200 rounded-lg px-2.5 py-1.5 text-xs text-notice-800">
  <strong>{m.pages.map(id => ALL_NAV_FLAT.find(n => n.id === id)?.label || id).join(", ")}</strong>: Not visible yet — saving will allow: {capLabel(m.cap)}
  </div>
  ))}
  {takesAway.length > 0 && (
  <div className="mt-2 bg-neutral-50 border border-neutral-200 rounded-lg px-2.5 py-1.5 text-xs text-neutral-600">
  Saving will take away: {takesAway.map(capLabel).join(", ")} — nothing ticked here needs it.
  </div>
  )}
  </div>
  )}

  {/* Rule 2: what no page stands for, on the same card. */}
  {isCustomizable && (
  <div className="border border-brand-100 rounded-xl p-3 bg-brand-50/20 mt-2">
  <div className="text-xs font-semibold text-neutral-700 mb-2">Also allowed to</div>
  {perm.error && <div className="text-xs text-danger-700 mb-2">Permissions could not be loaded: {perm.error}</div>}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5">
  {EXTRA_CAPS.map(c => (
  <label key={c.id} className="flex items-start gap-2 py-1 px-1 rounded hover:bg-brand-50 cursor-pointer text-xs text-neutral-600">
  <Checkbox checked={!!cardExtras[c.id]} onChange={e => { const v = e.target.checked; setCardExtras(x => ({ ...x, [c.id]: v })); }} aria-label={c.label} className="accent-brand-600 w-3.5 h-3.5 mt-0.5" />
  <span><span className={cardExtras[c.id] ? "text-neutral-700 font-medium" : ""}>{c.label}</span><span className="block text-2xs text-neutral-400">{c.help}</span></span>
  </label>
  ))}
  </div>
  </div>
  )}

  {/* Admin / Owner — fixed access notice */}
  {!isCustomizable && (
  <div className="bg-info-50 border border-info-100 rounded-xl p-2.5 text-xs text-info-700">
  {form.role === "admin"
    ? <><strong>Admins have everything.</strong> Every page and every permission; nothing to tick.</>
    : <><strong>{ROLES[form.role]?.label}</strong> has fixed access and cannot be customized.</>}
  </div>
  )}

  {/* Sign-in: a lost authenticator is reset here (2026-10-23). */}
  {editingUser && editingUser._memberStatus === "active" && (
  <div className="mt-3 flex items-center justify-between gap-2 bg-neutral-50 border border-neutral-200 rounded-xl p-2.5 text-xs text-neutral-600">
  <span>Lost or replaced the phone with their authenticator app? Reset it, and they set up a new one at their next sign-in.</span>
  <Btn size="xs" variant="secondary" onClick={() => resetMfa(editingUser)}>Reset authenticator</Btn>
  </div>
  )}

  <div className="flex gap-2 mt-3">
  <Btn
    size="sm"
    onClick={saveUser}
  >
    {editingUser ? "Save Changes" : "Add User"}
  </Btn>
  <Btn size="sm" variant="secondary" onClick={() => { setShowForm(false); setEditingUser(null); setForm({ email: "", role: "office_assistant", name: "", first_name: "", mi: "", last_name: "", manager_email: "" }); setCustomPages([]); }}>
  Cancel
  </Btn>
  </div>
  </div>
  );

  return (
  <div>
  <div className="flex items-center justify-between mb-5">
  <div>
  <PageHeader title="Team & Role Management" />
  <p className="text-xs text-neutral-400 mt-0.5">Add team members and choose exactly which modules they can access</p>
  </div>
  <Btn onClick={startAdd}>+ Add User</Btn>
  </div>

  {/* Role legend */}
  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5">
  {Object.entries(ROLES).map(([key, r]) => (
  <div key={key} className="bg-white rounded-xl border border-neutral-200 p-3 text-center">
  <div className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold text-white mb-1 ${r.color}`}>{r.label}</div>
  <div className="text-xs text-neutral-400">{key === "admin" ? "Full access" : key === "tenant" ? "Portal only" : "Customizable"}</div>
  </div>
  ))}
  </div>

  {/* Add form (Edit opens inside the person's card) */}
  {!editingUser && formPanel}

  {/* User list */}
  <div className="space-y-3">
  {users.map(u => {
  const effectivePages = getEffectivePages(u);
  // A saved card that ticks a page this person cannot see yet (rule 5).
  const uRole = u._memberRole || u.role;
  const listNotYet = u.custom_pages && CUSTOMIZABLE_ROLES.includes(uRole) && perm.defaults
    ? notVisibleYet(cardPages(effectivePages, ALL_NAV), personCapabilities(uRole, u.email, perm.rows, perm.defaults))
    : [];
  return (
  <div key={u.id} className="bg-white rounded-xl border border-neutral-200 shadow-card p-4">
  <div className="flex justify-between items-center">
  <div className="flex items-center gap-3">
  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold ${ROLES[u.role]?.color || "bg-neutral-400"}`}>
  {u.name?.[0]}
  </div>
  <div>
  <div className="font-semibold text-neutral-800 text-sm">{u.name}</div>
  <div className="text-xs text-neutral-400">{u.email}</div>
  </div>
  </div>
  <div className="flex flex-wrap items-center justify-end gap-2">
  <span className={`text-xs font-semibold text-white px-2 py-0.5 rounded-full ${ROLES[u.role]?.color || "bg-neutral-400"}`}>
  {ROLES[u.role]?.label}
  </span>
  {/* The invitation's state, then what can be done about it. Sending takes
      seconds, so the button says so (a button that looks untouched for
      that long reads as broken and gets clicked again). */}
  {(() => {
  const chip = inviteChip(u._member);
  const busy = !!inviting["inviteUser:" + (u.id || u.email || "")];
  const tone = { positive: "text-positive-700 bg-positive-50", info: "text-info-700 bg-info-50", warn: "text-warn-700 bg-warn-50", neutral: "text-neutral-600 bg-neutral-100" }[chip.tone];
  const label = { resend: "\u2709\uFE0F Resend", invite_again: "\u2709\uFE0F Invite again", invite: "\u2709\uFE0F Invite" };
  return <>
  <span data-testid="invite-chip" className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tone}`}>{chip.label}</span>
  {chip.actions.filter(a => a !== "cancel").map(a => (
  <Btn key={a} variant="secondary" size="xs" disabled={busy} onClick={() => inviteUser(u)}>{busy ? "Sending\u2026" : label[a]}</Btn>
  ))}
  {chip.actions.includes("cancel") && (
  <Btn variant="secondary" size="xs" disabled={busy} onClick={() => cancelInvite(u)}>Cancel invite</Btn>
  )}
  </>;
  })()}
  <Btn variant="secondary" size="xs" onClick={() => startEdit(u)}>
  ✏️ Edit
  </Btn>
  <Btn variant="danger" size="xs" onClick={() => removeUser(u.id, u.name, u.email)}>
  Remove
  </Btn>
  </div>
  </div>
  {editingUser && String(editingUser.id) === String(u.id) ? formPanel : <>
  {/* Show their current module access */}
  <div className="mt-3 flex flex-wrap gap-1">
  {effectivePages.map(p => {
  const nav = ALL_NAV_FLAT.find(n => n.id === p);
  return (
  <span key={p} className="inline-flex items-center gap-1 text-xs bg-brand-50 text-brand-700 border border-brand-100 px-2 py-0.5 rounded-full">
  {nav ? <><span className="material-icons-outlined text-xs">{nav.icon}</span>{nav.label}</> : p}
  </span>
  );
  })}
  </div>
  {u.custom_pages && (
  <div className="mt-1 text-xs text-neutral-400">Custom access · {effectivePages.length} modules</div>
  )}
  {listNotYet.map(m => (
  <div key={m.cap} className="mt-1 text-xs text-notice-700">
  {m.pages.map(id => ALL_NAV_FLAT.find(n => n.id === id)?.label || id).join(", ")}: Not visible yet — saving will allow: {capLabel(m.cap)}
  </div>
  ))}
  </>}
  </div>
  );
  })}
  {users.length === 0 && (
  <EmptyState size="inline" title={"No team members added yet. Click + Add User to get started."} />
  )}
  </div>
  </div>
  );
}


// ============ ARCHIVE (SOFT-DELETED ITEMS) ============
// An unaccepted invitation is never deleted: past its expires_at it reads
// "Expired" on the card and cannot be accepted (staff invitations, 2026-10-20).

function ArchivePage({ addNotification, userProfile, userRole, companyId, showConfirm, showToast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => { fetchArchived(); }, [companyId]);

  async function fetchArchived() {
  setLoading(true);
  const tables = [
  { name: "properties", label: "Property", fields: "id, address, type, status, archived_at, archived_by" },
  { name: "tenants", label: "Tenant", fields: "id, name, email, property, archived_at, archived_by" },
  { name: "work_orders", label: "Work Order", fields: "id, issue, property, status, archived_at" },
  { name: "documents", label: "Document", fields: "id, name, property, type, archived_at" },
  { name: "leases", label: "Lease", fields: "id, tenant_name, property, status, archived_at" },
  { name: "payments", label: "Payment", fields: "id, tenant, property, amount, archived_at" },
  { name: "vendors", label: "Vendor", fields: "id, name, email, phone, archived_at, archived_by" },
  { name: "hoa_payments", label: "HOA Payment", fields: "id, property, amount, due_date, status, archived_at, archived_by" },
  { name: "autopay_schedules", label: "Autopay Schedule", fields: "id, tenant, property, amount, archived_at, archived_by" },
  { name: "recurring_journal_entries", label: "Recurring Entry", fields: "id, description, status, archived_at, archived_by" },
  { name: "late_fee_rules", label: "Late Fee Rule", fields: "id, name, fee_type, fee_amount, archived_at, archived_by" },
  { name: "app_users", label: "Team Member", fields: "id, name, email, role, archived_at, archived_by" },
  { name: "doc_generated", label: "Generated Doc", fields: "id, template_name, property, archived_at, archived_by" },
  ];
  let all = [];
  for (const t of tables) {
  const { data } = await supabase.from(t.name).select(t.fields).eq("company_id", companyId).not("archived_at", "is", null).order("archived_at", { ascending: false });
  if (data) {
  all = all.concat(data.map(d => ({ ...d, _table: t.name, _label: t.label })));
  }
  }
  all.sort((a, b) => new Date(b.archived_at) - new Date(a.archived_at));
  setItems(all);
  setLoading(false);
  }

  async function restoreItem(item) {
  if (!await showConfirm({ message: `Restore this ${item._label.toLowerCase()}?` })) return;
  const { error } = await supabase.from(item._table).update({ archived_at: null, archived_by: null }).eq("id", item.id).eq("company_id", companyId);
  if (error) {
  pmError("PM-2004", { raw: error, context: "restore archived item" });
  return;
  }
  // If restoring a property, also offer to restore its archived tenant
  if (item._table === "properties" && item.address) {
  const { data: archivedTenants } = await supabase.from("tenants").select("id, name").eq("company_id", companyId).eq("property", item.address).not("archived_at", "is", null);
  if (archivedTenants?.length > 0) {
  const shouldRestore = await showConfirm({ message: `Found ${archivedTenants.length} archived tenant(s) for this property: ${archivedTenants.map(t => t.name).join(", ")}\n\nWould you like to restore them too?` });
  if (shouldRestore) {
  for (const t of archivedTenants) {
  const { error: tErr } = await supabase.from("tenants").update({ archived_at: null, archived_by: null, lease_status: "active" }).eq("id", t.id).eq("company_id", companyId);
  if (tErr) pmError("PM-3002", { raw: tErr, context: "restore tenant " + t.name, silent: true });
  }
  // Also restore associated leases
  const { error: lErr } = await supabase.from("leases").update({ archived_at: null, status: "active" }).eq("company_id", companyId).eq("property", item.address).not("archived_at", "is", null);
  if (lErr) pmError("PM-3004", { raw: lErr, context: "restore leases", silent: true });
  addNotification("♻️", `Restored property + ${archivedTenants.length} tenant(s)`);
  }
  }
  }
  // If restoring a tenant, update their property back to occupied
  if (item._table === "tenants" && item.property) {
  const { error: propErr } = await supabase.from("properties").update({ status: "occupied", tenant: item.name }).eq("company_id", companyId).eq("address", item.property).is("archived_at", null);
  if (propErr) pmError("PM-2002", { raw: propErr, context: "update property on restore", silent: true });
  }
  // Reactivate accounting class if restoring a property
  if (item._table === "properties" && item.address) {
  await supabase.from("acct_classes").update({ is_active: true }).eq("company_id", companyId).eq("name", item.address);
  }
  addNotification("♻️", `Restored ${item._label}: ${item.address || item.name || item.issue || item.tenant_name || item.tenant || "item"}`);
  fetchArchived();
  }

  async function permanentDelete(item) {
  if (!await showConfirm({ message: `PERMANENTLY delete this ${item._label.toLowerCase()}? This cannot be undone.`, variant: "danger", confirmText: "Delete" })) return;
  const { error } = await supabase.from(item._table).delete().eq("id", item.id).eq("company_id", companyId);
  const booked = bookedDeleteMessage(item._table, error);
  if (booked) { showToast(booked, "error"); return; }
  if (error) { pmError("PM-8006", { raw: error, context: "permanent delete" }); return; }
  logAudit("delete", item._table, "Permanently deleted " + item._label + ": " + (item.name || item.address || item.id), item.id, userProfile?.email, userRole, companyId);
  addNotification("🗑️", `Permanently deleted ${item._label}`);
  fetchArchived();
  }

  const filtered = filter === "all" ? items : items.filter(i => i._table === filter);
  const tables = [...new Set(items.map(i => i._table))];
  const daysUntilPurge = (item) => Math.max(0, 180 - Math.floor((new Date() - new Date(item.archived_at)) / 86400000));

  function getItemTitle(item) {
  return item.address || item.name || item.issue || item.tenant_name || item.tenant || item.description || item.template_name || "Unnamed";
  }

  function getItemSubtitle(item) {
  return item.property || item.email || item.type || item.status || item.fee_type || item.role || (item.due_date ? fmtDate(item.due_date) : "");
  }

  return (
  <div>
  <div className="flex items-center justify-between mb-5">
  <div>
  <PageHeader title="Archive" />
  <p className="text-xs text-neutral-400 mt-1">Archived items are auto-purged after 180 days</p>
  </div>
  <div className="text-sm text-neutral-400">{items.length} archived item{items.length !== 1 ? "s" : ""}</div>
  </div>

  <div className="flex gap-2 mb-4 flex-wrap">
  <FilterPill active={filter === "all"} onClick={() => setFilter("all")}>All ({items.length})</FilterPill>
  {tables.map(t => {
  const count = items.filter(i => i._table === t).length;
  const label = t.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase());
  return <FilterPill key={t} active={filter === t} onClick={() => setFilter(t)}><span className="capitalize">{label} ({count})</span></FilterPill>;
  })}
  </div>

  {loading ? <div className="text-center py-8 text-neutral-400">Loading...</div> : filtered.length === 0 ? (
  <div className="text-center py-16">
  <div className="text-4xl mb-3">📦</div>
  <div className="text-neutral-400">No archived items</div>
  <div className="text-xs text-neutral-300 mt-1">Deleted items will appear here for 180 days</div>
  </div>
  ) : (
  <div className="space-y-2">
  {filtered.map(item => (
  <div key={item._table + item.id} className="bg-white rounded-xl border border-neutral-200 shadow-card p-4 flex items-center gap-4">
  <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center text-lg">
  {item._table === "properties" ? "🏠" : item._table === "tenants" ? "👤" : item._table === "work_orders" ? "🔧" : item._table === "documents" || item._table === "doc_generated" ? "📄" : item._table === "leases" ? "📋" : item._table === "vendors" ? "🏗️" : item._table === "hoa_payments" ? "🏘️" : item._table === "autopay_schedules" ? "🔄" : item._table === "recurring_journal_entries" ? "📊" : item._table === "late_fee_rules" ? "⚠️" : item._table === "app_users" ? "👥" : "💰"}
  </div>
  <div className="flex-1 min-w-0">
  <div className="font-semibold text-neutral-800 text-sm">{getItemTitle(item)}</div>
  <div className="text-xs text-neutral-400">{item._label} · {getItemSubtitle(item)}</div>
  <div className="text-xs text-neutral-300 mt-0.5">Archived {fmtDate(item.archived_at)} {item.archived_by ? "by " + item.archived_by : ""} · <span className={daysUntilPurge(item) < 30 ? "text-danger-400 font-semibold" : "text-neutral-400"}>{daysUntilPurge(item)} days until auto-purge</span></div>
  </div>
  <div className="flex gap-2 shrink-0">
  <Btn variant="success" size="sm" onClick={() => restoreItem(item)}>♻️ Restore</Btn>
  <Btn variant="danger" size="sm" onClick={() => permanentDelete(item)}>🗑️ Delete</Btn>
  </div>
  </div>
  ))}
  </div>
  )}
  </div>
  );
}


// ============ TASKS & APPROVALS ============
// Three things, kept apart (Sahil, 2026-10-13): APPROVALS someone is waiting
// on, TASKS a record is missing (computed live by open_tasks), and DEADLINES
// from the lease clock. The sidebar badge counts the same loader
// (utils/openWork.js), so the number and the page always agree.
//
// Three ways to look at it (Sahil, 2026-10-21), remembered per person on
// their sign-in account (prefs.tasks_view, utils/userPrefs.js):
//   By type      a work queue: pick a kind on the left, clear it in a table,
//                many at once (month to month, waive)
//   By urgency   decisions, overdue, this week, later; backlog folded away
//   By property  one tile per property; a tile opens its items
// Old paperwork (isBacklog) is kept apart in all three so it stops drowning
// out what is due now. Every line, in every view, is drawn by WorkRow with
// the same buttons (WorkActions), so an action behaves the same everywhere.
const TASKS_VIEW_LABELS = [["type", "By type"], ["urgency", "By urgency"], ["property", "By property"]];
const HOT = (e) => e.item?.severity === "high" || e.item?.severity === "overdue";

/** The words on a line: who / where / what, whichever view draws it. */
function entryText(e, ctx) {
  const it = e.item;
  if (e.type === "approval") return { who: it.title, where: e.address ? propertyLabel(e.address) : "", what: it.subtitle, detail: "", title: it.title, sub: it.subtitle };
  if (e.type === "deadline") {
    const [head, ...rest] = String(it.title || "").split(": ");
    const named = it.tenant_id != null && rest.length > 0;
    return { who: named ? head : (CLOCK_KINDS[it.kind]?.label || "Deadline"), where: e.address ? propertyLabel(e.address) : "", what: named ? rest.join(": ") : it.title, detail: it.detail, title: it.title, sub: it.detail };
  }
  const g = ctx.groupOf(it) || {};
  const company = g.group === "company" || TASK_KINDS[it.kind]?.group === "company";
  const what = it.kind === "movein_doc" ? TASK_DOC_LABEL[it.subject] || it.title.split(": ").slice(1).join(": ") : company ? it.title : it.title.split(": ").slice(1).join(": ") || it.title;
  return {
    who: company ? (TASK_KINDS[it.kind]?.label || it.title) : g.name || it.title, where: company ? "" : propertyLabel(it.address) || it.address,
    what, detail: it.detail, title: it.title, sub: [company ? "" : propertyLabel(it.address), it.detail].filter(Boolean).join(" · "),
  };
}

/** The buttons for one line; the same in all three views. */
function WorkActions({ entry, ctx }) {
  const it = entry.item;
  if (entry.type === "approval") return (
    <>
      <Btn variant="success-fill" size="xs" onClick={() => ctx.onApproval(it, "approve")}>Approve</Btn>
      <Btn variant="danger" size="xs" onClick={() => ctx.onApproval(it, "reject")}>Reject</Btn>
    </>
  );
  if (entry.type === "deadline") {
    const act = clockAction(it);
    return (
      <>
        {act && <Btn variant="primary" size="xs" onClick={() => ctx.openDeadline(it, act)}>{act.label}</Btn>}
        <Btn variant="ghost" size="xs" onClick={() => ctx.onDismissDeadline(it)}>Dismiss</Btn>
      </>
    );
  }
  const group = ctx.groupOf(it);
  const meta = TASK_KINDS[it.kind] || {};
  const pending = ctx.isPending(it);
  return (
    <>
      {it.action && <Btn variant="primary" size="xs" onClick={() => ctx.go(it)}>Open</Btn>}
      {it.kind === "movein_doc" && !pending && group && (ctx.canWaiveTenant
        ? <Btn variant="ghost" size="xs" onClick={() => ctx.onWaiveTenantDoc(group, it)}>Waive</Btn>
        : <Btn variant="ghost" size="xs" onClick={() => ctx.onRequest(group, it)}>Request waiver</Btn>)}
      {it.kind === "property_record" && !pending && group && (ctx.canWaiveProperty
        ? <Btn variant="ghost" size="xs" onClick={() => ctx.onWaiveProperty(group, it)}>Not needed</Btn>
        : <Btn variant="ghost" size="xs" onClick={() => ctx.onRequest(group, it)}>Request waiver</Btn>)}
      {meta.dismissable && ctx.canWaiveProperty && <Btn variant="ghost" size="xs" onClick={() => ctx.onDismiss(it)}>Month to month</Btn>}
    </>
  );
}

/**
 * One line. variant "table": a row of the By type table (checkbox, who,
 * property, what, buttons). variant "line": a one-line card (title,
 * subtitle, buttons) for By urgency and a property's panel.
 */
function WorkRow({ entry, ctx, variant = "line", selectable = false, checked = false, onCheck }) {
  const x = entryText(entry, ctx);
  const hot = HOT(entry);
  const pending = entry.type === "task" && ctx.isPending(entry.item);
  const pendingNote = pending && <div className="text-2xs text-warn-700 mt-0.5 font-semibold uppercase tracking-wide">Waiver requested — awaiting review</div>;
  if (variant === "table") return (
    <tr className="border-b border-neutral-100 align-middle" data-task-kind={entry.item.kind || entry.type}>
      <td className={"pl-3 pr-1 py-2.5 w-8 border-l-4 " + (hot ? "border-l-danger-400" : "border-l-transparent")}>
        {selectable && <Checkbox checked={checked} onChange={e => onCheck(entry, e.target.checked)} aria-label={"Select " + x.who} />}
      </td>
      <td className="px-2 py-2.5 text-sm font-semibold text-neutral-800">{x.who}</td>
      <td className="px-2 py-2.5 text-xs text-neutral-500">{x.where}</td>
      <td className="px-2 py-2.5 text-sm text-neutral-700">
        <div>{x.what}</div>
        {x.detail && <div className="text-2xs text-neutral-400 mt-0.5">{x.detail}</div>}
        {pendingNote}
      </td>
      <td className="px-2 py-2.5"><div className="flex gap-1.5 justify-end flex-wrap"><WorkActions entry={entry} ctx={ctx} /></div></td>
    </tr>
  );
  return (
    <div className={"bg-white rounded-xl border border-brand-50 border-l-4 px-3 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 " + (hot ? "border-l-danger-400" : "border-l-brand-50")} data-task-kind={entry.item.kind || entry.type}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-neutral-800">{x.title}</div>
        {x.sub && <div className="text-2xs text-neutral-400 mt-0.5">{x.sub}</div>}
        {pendingNote}
      </div>
      <div className="flex gap-1.5 flex-wrap shrink-0"><WorkActions entry={entry} ctx={ctx} /></div>
    </div>
  );
}

/** View A: a rail of kinds on the left, the chosen kind as a table on the right. */
function TasksByType({ entries, ctx, railKey, setRailKey, selected, setSelected }) {
  const rail = railEntries(entries);
  const all = [...rail.decision, ...rail.now, ...rail.backlog];
  const current = all.find(r => r.key === railKey) || all[0];
  if (!current) return null;
  const rows = entries.filter(e => entryRailKey(e) === current.key);
  // The batch buttons: month to month for leases ended, waive for move-in
  // documents (only for those who may waive; others request one by one).
  const canMtm = (e) => e.type === "task" && TASK_KINDS[e.item.kind]?.dismissable && ctx.canWaiveProperty;
  const canWaive = (e) => e.type === "task" && e.item.kind === "movein_doc" && ctx.canWaiveTenant && !ctx.isPending(e.item) && !!ctx.groupOf(e.item);
  const selectable = (e) => canMtm(e) || canWaive(e);
  const anySelectable = rows.some(selectable);
  const picked = rows.filter(e => selected.has(e.id));
  const mtm = picked.filter(canMtm), waive = picked.filter(canWaive);
  const toggle = (e, on) => setSelected(prev => { const n = new Set(prev); if (on) n.add(e.id); else n.delete(e.id); return n; });
  const allOn = anySelectable && rows.filter(selectable).every(e => selected.has(e.id));
  const pick = (k) => { setRailKey(k); setSelected(new Set()); };
  const railGroup = (title, list) => list.length > 0 && (
    <div className="contents md:block">
      <div className="w-full text-2xs font-bold uppercase tracking-wide text-neutral-400 px-2 pt-3 pb-1">{title}</div>
      {list.map(r => (
        <button key={r.key} type="button" onClick={() => pick(r.key)} aria-pressed={r.key === current.key}
          className={"flex items-center justify-between gap-2 text-left text-sm px-2.5 py-1.5 rounded-lg md:w-full " + (r.key === current.key ? "bg-brand-50 text-brand-700 font-semibold" : "text-neutral-700 hover:bg-neutral-50")}>
          <span>{r.label}</span>
          <span className={"text-2xs font-bold px-1.5 py-0.5 rounded-full " + (r.key === "approvals" ? "bg-danger-100 text-danger-700" : "bg-neutral-100 text-neutral-500")}>{r.count}</span>
        </button>
      ))}
    </div>
  );
  return (
    <div className="bg-white rounded-xl border border-brand-50 md:grid md:grid-cols-[230px_minmax(0,1fr)]" data-testid="tasks-by-type">
      <nav className="flex flex-wrap gap-1 p-2 border-b border-brand-50 md:block md:border-b-0 md:border-r" aria-label="Task types" data-testid="tasks-rail">
        {railGroup("Needs your decision", rail.decision)}
        {railGroup("Do now", rail.now)}
        {railGroup("Backlog", rail.backlog)}
      </nav>
      <div className="p-3 md:p-4 min-w-0">
        <div className="flex items-baseline gap-2 mb-3 flex-wrap">
          <h3 className="text-base font-bold text-neutral-800">{current.label}</h3>
          <span className="text-xs text-neutral-400">{current.count} {current.count === 1 ? "item" : "items"}</span>
        </div>
        {picked.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 bg-brand-50 border border-brand-200 rounded-xl px-3 py-2 mb-3 text-sm font-medium text-brand-800" data-testid="tasks-bulk-bar">
            <span>{picked.length} selected</span>
            {mtm.length > 0 && <Btn variant="secondary" size="xs" onClick={() => ctx.bulkMonthToMonth(mtm).then(() => setSelected(new Set()))}>Mark month to month</Btn>}
            {waive.length > 0 && <Btn variant="secondary" size="xs" onClick={() => ctx.bulkWaive(waive).then(() => setSelected(new Set()))}>Waive selected</Btn>}
            <Btn variant="ghost" size="xs" onClick={() => setSelected(new Set())}>Clear</Btn>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-2xs uppercase tracking-wide text-neutral-400">
                <th className="pl-3 pr-1 py-2 w-8 border-l-4 border-l-transparent">
                  {anySelectable && <Checkbox checked={allOn} onChange={e => setSelected(e.target.checked ? new Set(rows.filter(selectable).map(r => r.id)) : new Set())} aria-label="Select all" />}
                </th>
                <th className="px-2 py-2 font-semibold">Who / what</th>
                <th className="px-2 py-2 font-semibold">Property</th>
                <th className="px-2 py-2 font-semibold">Detail</th>
                <th className="px-2 py-2 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(e => <WorkRow key={e.id} entry={e} ctx={ctx} variant="table" selectable={selectable(e)} checked={selected.has(e.id)} onCheck={toggle} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** A heading with a count and a rule, for the By urgency sections. */
function UrgencyHeading({ label, count, tone, children }) {
  return (
    <div className={"flex items-center gap-2 mt-5 mb-2 text-xs font-bold uppercase tracking-wide " + tone}>
      <span>{label} · {count}</span>
      <span className="flex-1 h-px bg-neutral-200" />
      {children}
    </div>
  );
}

/** View B: what needs a decision, then what is overdue, this week, later; backlog folded. */
function TasksByUrgency({ entries, ctx, today }) {
  const [showBacklog, setShowBacklog] = useState(false);
  const b = urgencyBuckets(entries, today);
  const sections = [["decision", "Needs your decision", "text-danger-600"], ["overdue", "Overdue", "text-danger-600"], ["week", "This week", "text-warn-700"], ["later", "Later", "text-neutral-500"]];
  return (
    <div data-testid="tasks-by-urgency">
      {sections.map(([k, label, tone]) => b[k].length > 0 && (
        <div key={k} data-testid={"urgency-" + k}>
          <UrgencyHeading label={label} count={b[k].length} tone={tone} />
          <div className="space-y-1.5">{b[k].map(e => <WorkRow key={e.id} entry={e} ctx={ctx} />)}</div>
        </div>
      ))}
      {b.backlog.length > 0 && (
        <div data-testid="urgency-backlog">
          <UrgencyHeading label="Backlog" count={b.backlog.length} tone="text-neutral-400">
            <Btn variant="ghost" size="xs" onClick={() => setShowBacklog(s => !s)}>{showBacklog ? "Hide" : "Show"}</Btn>
          </UrgencyHeading>
          {showBacklog
            ? <div className="space-y-1.5">{b.backlog.map(e => <WorkRow key={e.id} entry={e} ctx={ctx} />)}</div>
            : <div className="rounded-xl border border-dashed border-neutral-200 px-3 py-2.5 text-xs text-neutral-400">Older tenancies' move-in documents and property setup records. Not urgent.</div>}
        </div>
      )}
    </div>
  );
}

/** View C: one tile per property; a tile opens its items in a panel. */
function TasksByProperty({ entries, ctx, today }) {
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  const tiles = groupByProperty(onlyOverdue ? entries.filter(e => isOverdueEntry(e, today)) : entries);
  const open = openKey ? tiles.find(t => t.key === openKey) : null;
  const chipCls = (e) => e.type === "approval" ? "bg-brand-50 text-brand-700" : isOverdueEntry(e, today) ? "bg-danger-100 text-danger-700" : HOT(e) ? "bg-warn-100 text-warn-700" : "bg-neutral-100 text-neutral-600";
  return (
    <div data-testid="tasks-by-property">
      <div className="flex justify-end mb-3">
        <FilterPill active={onlyOverdue} onClick={() => setOnlyOverdue(v => !v)}>Only overdue</FilterPill>
      </div>
      {tiles.length === 0 && <div className="text-sm text-neutral-400 py-8 text-center">Nothing overdue.</div>}
      <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(230px,1fr))]">
        {tiles.map(t => (
          <button key={t.key} type="button" onClick={() => setOpenKey(t.key)} data-testid="task-tile"
            className="text-left bg-white rounded-xl border border-brand-50 p-3 hover:border-brand-300 transition-colors flex flex-col gap-2">
            <div className="text-sm font-bold text-neutral-800">{t.label}</div>
            <div className="text-xs text-neutral-400">{t.company ? "Company-wide" : t.tenants.join(", ") || "No tenant listed"}</div>
            <div className="flex flex-wrap gap-1.5">
              {t.entries.slice(0, 8).map(e => <span key={e.id} className={"text-2xs font-semibold px-2 py-0.5 rounded-full " + chipCls(e)}>{chipLabel(e)}</span>)}
              {t.entries.length > 8 && <span className="text-2xs text-neutral-400 px-1 py-0.5">+{t.entries.length - 8} more</span>}
            </div>
          </button>
        ))}
      </div>
      {open && (
        <Modal title={open.label} onClose={() => setOpenKey(null)}>
          {!open.company && open.address !== open.label && <div className="text-xs text-neutral-400 -mt-3 mb-3">{open.address}</div>}
          <div className="space-y-1.5">{open.entries.map(e => <WorkRow key={e.id} entry={e} ctx={ctx} />)}</div>
        </Modal>
      )}
    </div>
  );
}

function TasksAndApprovals({ companyId, setPage, showToast, showConfirm, userProfile, userRole, addNotification, allowedPages }) {
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [work, setWork] = useState({ approvals: [], tasks: [], groups: [], deadlines: [], pendingDocRequests: [], tenantProperty: {}, counts: { approvals: 0, tasks: 0, deadlines: 0, total: 0 } });
  const [error, setError] = useState("");
  const [view, setView] = useState("type");
  const [railKey, setRailKey] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  // The person picked a view before their saved one arrived: theirs wins.
  const viewChosen = useRef(false);

  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [companyId]);
  useEffect(() => {
    let off = false;
    loadAccountPref(supabase, "tasks_view").then(r => {
      if (off || viewChosen.current) return;
      if (!r.ok) { pmError("PM-8006", { raw: r.error, context: "loading the tasks view", silent: true }); return; }
      if (TASKS_VIEWS.includes(r.value)) setView(r.value);
    });
    return () => { off = true; };
  }, []);

  // Switches at once; saved in the background. A failed save only means
  // the next visit opens on the old view.
  function chooseView(v) {
    viewChosen.current = true;
    setView(v);
    saveAccountPref(supabase, "tasks_view", v)
      .then(r => { if (!r.ok) pmError("PM-8006", { raw: r.error, context: "saving the tasks view", silent: true }); },
        e => pmError("PM-8006", { raw: e, context: "saving the tasks view (network)", silent: true }));
  }

  async function fetchAll() {
    setLoading(true); setError("");
    try {
      const w = await loadOpenWork(supabase, { companyId, userRole, userEmail: userProfile?.email || "", allowedPages, pageAll: fetchAllPaged });
      setWork(w);
      setLoaded(true);
    } catch (e) {
      pmError("PM-8006", { raw: e, context: "open work", silent: true });
      setError("The list could not be loaded: " + (e?.message || e));
    }
    setLoading(false);
  }

  // ---- tasks: waive / request / dismiss ----
  // `reason` given = one of a batch ("Waive selected"): asked once by the
  // caller, and the caller toasts and refreshes once at the end.
  async function waiveTenantDoc(group, t, reason = null) {
    const label = TASK_DOC_LABEL[t.subject] || t.subject;
    let why = reason;
    if (why == null) {
      const ans = await showConfirm({ title: "Waive " + label + " for " + group.name + "?", message: "The requirement is marked as met without a file. Say why, for the record.", fields: [{ key: "reason", label: "Reason", type: "text", default: "", required: true }], confirmText: "Waive" });
      if (!ans || !String(ans.reason || "").trim()) return { ok: false };
      why = ans.reason;
    }
    const r = await waiveTenantDoc_(supabase, companyId, group.tenantId, label);
    if (!r.ok) { if (reason == null) showToast("Could not record the waiver: " + r.error.message, "error"); return r; }
    logAudit("approve", "tenants", "Waived " + label + " for " + group.name + ": " + String(why).trim(), String(group.tenantId), userProfile?.email, userRole, companyId);
    if (reason == null) { showToast(label + " waived for " + group.name + ".", "success"); fetchAll(); }
    return { ok: true };
  }
  async function waiveProperty(group, t) {
    const label = PROPERTY_RECORD_LABELS[t.subject] || t.subject;
    const ans = await showConfirm({ title: label + " not needed for " + group.name + "?", message: "The item stops showing for this property. Say why, for the record.", fields: [{ key: "reason", label: "Reason", type: "text", default: "", required: true }], confirmText: "Not needed" });
    if (!ans || !String(ans.reason || "").trim()) return;
    const r = await waivePropertyRecord(supabase, companyId, group.propertyId, t.subject);
    if (!r.ok) { showToast("Could not record it: " + r.error.message, "error"); return; }
    logAudit("approve", "properties", label + " marked not needed for " + group.address + ": " + String(ans.reason).trim(), String(group.propertyId), userProfile?.email, userRole, companyId);
    showToast(label + " marked not needed.", "success");
    fetchAll();
  }
  async function requestWaiver(group, t) {
    const isDoc = t.kind === "movein_doc";
    const label = isDoc ? (TASK_DOC_LABEL[t.subject] || t.subject) : (PROPERTY_RECORD_LABELS[t.subject] || t.subject);
    const ans = await showConfirm({ title: "Ask to waive " + label + "?", message: "Your manager (or an admin) reviews it on this page.", fields: [{ key: "reason", label: "Reason", type: "text", default: "", required: true }], confirmText: "Send request" });
    if (!ans || !String(ans.reason || "").trim()) return;
    try {
      const approver = await defaultApprover(supabase, companyId, userProfile?.email);
      const { error } = await supabase.from("doc_exception_requests").insert([{
        company_id: companyId, tenant_name: isDoc ? group.name : "Setup: " + group.address, property: group.address,
        doc_type: isDoc ? label : t.subject, reason: String(ans.reason).trim(), requested_by: userProfile?.email || "", approver_email: approver,
      }]);
      if (error) throw error;
      if (approver && addNotification) addNotification("📋", `${userProfile?.email || "Staff"} asked to waive ${label} — ${isDoc ? group.name : propertyLabel(group.address)}`, { recipient: approver, type: "doc_exception" });
      logAudit("request", isDoc ? "tenants" : "properties", "Waiver requested: " + label + " for " + (isDoc ? group.name : group.address), "", userProfile?.email, userRole, companyId);
      showToast("Request sent.", "success");
      fetchAll();
    } catch (e) { showToast("Could not send the request: " + (e.message || e), "error"); }
  }
  // `batch` = one of "Mark month to month" on several: the caller asked once.
  async function dismissTask(t, batch = false) {
    if (!batch && !await showConfirm({ title: "Carrying on month to month?", message: t.title + ". This stops showing; the lease clock still flags a renewal when one is due.", confirmText: "Month to month" })) return { ok: false };
    const r = await dismissOpenItem(supabase, companyId, t.id, userProfile?.email);
    if (!r.ok) { if (!batch) showToast("Could not dismiss it: " + r.error.message, "error"); return r; }
    logAudit("update", "leases", "Marked month to month: " + t.title, t.leaseId || "", userProfile?.email, userRole, companyId);
    if (!batch) fetchAll();
    return { ok: true };
  }

  // ---- batches (By type): ONE question, then the same function per row ----
  async function runBatch(list, fn, doneText) {
    let done = 0; const failed = [];
    for (const e of list) {
      const r = await fn(e);
      if (r?.ok) done++; else failed.push(r?.error?.message || "failed");
    }
    if (failed.length) showToast(done + " " + doneText + "; " + failed.length + " could not be saved: " + failed[0], "error");
    else showToast(done + " " + doneText + ".", "success");
    fetchAll();
  }
  async function bulkMonthToMonth(list) {
    if (!list.length) return;
    if (!await showConfirm({ title: "Carry " + list.length + (list.length === 1 ? " tenant" : " tenants") + " on month to month?", message: "These stop showing; the lease clock still flags a renewal when one is due.", confirmText: "Month to month" })) return;
    await runBatch(list, e => dismissTask(e.item, true), "marked month to month");
  }
  async function bulkWaive(list) {
    if (!list.length) return;
    const ans = await showConfirm({ title: "Waive " + list.length + (list.length === 1 ? " document" : " documents") + "?", message: "Each requirement is marked as met without a file. Say why, for the record; the same reason is kept on each.", fields: [{ key: "reason", label: "Reason", type: "text", default: "", required: true }], confirmText: "Waive" });
    if (!ans || !String(ans.reason || "").trim()) return;
    await runBatch(list, e => waiveTenantDoc(groupById.get(e.item.id), e.item, String(ans.reason).trim()), "waived");
  }

  // ---- approvals ----
  async function handleApproval(item, action) {
    if (!guardSubmit("approval", item.id)) return;
    try { await decideApproval(item, action); } finally { guardRelease("approval", item.id); }
  }
  async function decideApproval(item, action) {
    const r = item.data;
    if (item.type === "property") {
      if (action === "approve") {
        if (r.request_type === "delete") { showToast("A property delete asks for a reason on the Properties page. Opening it.", "info"); setPage("properties", { openProperty: r.address, returnTo: TASKS_RETURN }); return; }
        const out = await approvePropertyRequest(r, { companyId, userProfile, userRole, showToast, showConfirm, addNotification });
        if (!out.ok) return;
        showToast("Approved: " + item.title, "success");
      } else {
        const out = await rejectPropertyRequest(r, { companyId, userProfile, userRole, showToast, addNotification });
        if (!out.ok) return;
        showToast("Rejected: " + item.title, "info");
      }
    } else if (item.type === "document") {
      const { error: revErr } = await supabase.from("doc_exception_requests").update({ status: action === "approve" ? "approved" : "rejected", reviewed_by: userProfile?.email, reviewed_at: new Date().toISOString() }).eq("id", r.id).eq("company_id", companyId);
      if (revErr) { showToast("Not " + (action === "approve" ? "approved" : "rejected") + ": " + revErr.message, "error"); return; }
      if (action === "approve") {
        if (item.setup) {
          // A property record "not needed" -- used to find no tenant named
          // "Setup: …", do nothing, and leave the task forever.
          const { data: p } = await supabase.from("properties").select("id").eq("company_id", companyId).eq("address", r.property).is("archived_at", null).maybeSingle();
          if (!p) { showToast("Approved, but the property was not found; nothing was waived.", "error"); return; }
          const w = await waivePropertyRecord(supabase, companyId, p.id, r.doc_type);
          if (!w.ok) { showToast("Approved, but the waiver could not be recorded: " + w.error.message, "error"); return; }
        } else if (r.doc_type) {
          // By tenant id (two tenants can share a name).
          const { data: tRows } = await supabase.from("tenants").select("id, property").eq("company_id", companyId).eq("name", r.tenant_name).is("archived_at", null).limit(5);
          const t = (tRows || []).length === 1 ? tRows[0] : (tRows || []).find(x => sameAddress(x.property, r.property));
          if (!t) { showToast("Approved, but the tenant could not be identified; waive it from their page.", "error"); return; }
          const w = await waiveTenantDoc_(supabase, companyId, t.id, r.doc_type);
          if (!w.ok) { showToast("Approved, but the waiver could not be recorded: " + w.error.message, "error"); return; }
        }
        showToast("Approved: " + item.title, "success");
        logAudit("approve", item.setup ? "properties" : "tenants", "Waiver approved: " + item.title + (r.reason ? " (" + r.reason + ")" : ""), r.id, userProfile?.email, userRole, companyId);
        if (r.requested_by && addNotification) addNotification("✅", "Your waiver request was approved: " + item.title, { recipient: r.requested_by, type: "doc_exception" });
      } else {
        showToast("Rejected: " + item.title, "info");
        logAudit("reject", item.setup ? "properties" : "tenants", "Waiver rejected: " + item.title, r.id, userProfile?.email, userRole, companyId);
        if (r.requested_by && addNotification) addNotification("❌", "Your waiver request was rejected: " + item.title, { recipient: r.requested_by, type: "doc_exception" });
      }
    } else if (item.type === "member") {
      const { error } = action === "approve"
        ? await supabase.rpc("approve_member_request", { p_member_id: r.id, p_role: r.role || null })
        : await supabase.rpc("handle_membership_request", { p_member_id: r.id, p_action: "reject", p_role: null });
      if (error) { showToast("Not " + (action === "approve" ? "approved" : "rejected") + ": " + error.message, "error"); return; }
      showToast((action === "approve" ? "Approved" : "Rejected") + " join request for " + r.user_email, action === "approve" ? "success" : "info");
    }
    fetchAll();
  }

  async function dismissDeadline(item) {
    if (!await showConfirm({ title: "Dismiss this?", message: item.title + ". It will not come back.", confirmText: "Dismiss" })) return;
    const r = await dismissOpenItem(supabase, companyId, item.item_key, userProfile?.email);
    if (!r.ok) { showToast("Could not dismiss it: " + r.error.message, "error"); return; }
    fetchAll();
  }

  // The card (tenant or property) each task belongs to: the waive and
  // request functions take it, as they did when the page was cards.
  const groupById = React.useMemo(() => {
    const m = new Map();
    for (const g of work.groups || []) for (const t of g.lines) m.set(t.id, g);
    return m;
  }, [work.groups]);
  const entries = React.useMemo(() => toEntries(work), [work]);

  if (loading && !loaded) return <Spinner />;
  const { counts } = work;
  const today = formatLocalDate(new Date());
  const pendingFor = (group, t) => (work.pendingDocRequests || []).some(r => t.kind === "movein_doc"
    ? (r.tenant_name === group.name && r.doc_type === TASK_DOC_LABEL[t.subject])
    : (String(r.tenant_name || "").startsWith("Setup: ") && sameAddress(r.property, group.address) && r.doc_type === t.subject));
  const go = (t) => { const w = withReturn(t.action, t); if (w) setPage(w.page, w.action); };
  const ctx = {
    canWaiveTenant: userRole === "admin" || userRole === "owner",
    canWaiveProperty: ["admin", "owner", "manager"].includes(userRole),
    groupOf: (t) => groupById.get(t.id) || null,
    isPending: (t) => { const g = groupById.get(t.id); return !!g && (t.kind === "movein_doc" || t.kind === "property_record") && pendingFor(g, t); },
    go,
    openDeadline: (d, act) => { const w = withReturn({ page: act.page, ...(act.action || {}) }, d); if (w) setPage(w.page, w.action); },
    onApproval: handleApproval, onDismissDeadline: dismissDeadline,
    onWaiveTenantDoc: waiveTenantDoc, onWaiveProperty: waiveProperty, onRequest: requestWaiver, onDismiss: dismissTask,
    bulkMonthToMonth, bulkWaive,
  };
  const backlogN = entries.filter(e => e.backlog).length;
  const nowN = entries.filter(e => e.type !== "approval" && !e.backlog).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <PageHeader title="Tasks & Approvals" />
        <Btn variant="ghost" size="xs" onClick={fetchAll} busy={loading} busyLabel="Refreshing…">Refresh</Btn>
      </div>
      {error && <div className="mb-4 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">{error}</div>}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="inline-flex rounded-lg border border-neutral-200 bg-white p-0.5" role="group" aria-label="View" data-testid="tasks-view-switch">
          {TASKS_VIEW_LABELS.map(([id, label]) => (
            <button key={id} type="button" aria-pressed={view === id} onClick={() => chooseView(id)}
              className={"px-3 py-1.5 text-xs font-semibold rounded-md whitespace-nowrap transition-colors " + (view === id ? "bg-brand-600 text-white" : "text-neutral-600 hover:bg-neutral-100")}>{label}</button>
          ))}
        </div>
        <span className="text-xs text-neutral-500" data-testid="tasks-summary">{nowN} need action · {backlogN} in backlog · {counts.approvals} {counts.approvals === 1 ? "approval" : "approvals"}</span>
      </div>

      {counts.total > 0 && view === "type" && <TasksByType entries={entries} ctx={ctx} railKey={railKey} setRailKey={setRailKey} selected={selected} setSelected={setSelected} />}
      {counts.total > 0 && view === "urgency" && <TasksByUrgency entries={entries} ctx={ctx} today={today} />}
      {counts.total > 0 && view === "property" && <TasksByProperty entries={entries} ctx={ctx} today={today} />}

      {counts.total === 0 && !error && (
        <div className="text-center py-16">
          <span className="material-icons-outlined text-5xl text-neutral-200 mb-3">task_alt</span>
          <div className="text-lg font-semibold text-neutral-400">All caught up!</div>
          <div className="text-sm text-neutral-300">Nothing to approve, fix or watch</div>
        </div>
      )}
    </div>
  );
}

// ============ ERROR LOG DASHBOARD ============
function ErrorLogDashboard({ companyId, showToast }) {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ module: "", severity: "", reported: false, unresolved: true, range: "7d" });
  const [stats, setStats] = useState({ critical: 0, error: 0, reported: 0, resolved: 0 });
  const [runningCheck, setRunningCheck] = useState(false);
  const [violations, setViolations] = useState([]);

  useEffect(() => { fetchErrors(); }, [filter]);

  async function fetchErrors() {
    setLoading(true);
    let q = supabase.from("error_log").select("*").eq("company_id", companyId).order("created_at", { ascending: false }).limit(200);
    if (filter.module) q = q.eq("module", filter.module);
    if (filter.severity) q = q.eq("severity", filter.severity);
    if (filter.reported) q = q.eq("reported_by_user", true);
    if (filter.unresolved) q = q.eq("resolved", false);
    const rangeMap = { "1d": 1, "7d": 7, "30d": 30 };
    if (rangeMap[filter.range]) {
      const since = new Date(); since.setDate(since.getDate() - rangeMap[filter.range]);
      q = q.gte("created_at", since.toISOString());
    }
    const { data } = await q;
    setErrors(data || []);
    // Fetch 24h stats
    const since24h = new Date(); since24h.setDate(since24h.getDate() - 1);
    const since7d = new Date(); since7d.setDate(since7d.getDate() - 7);
    // COUNTED, not fetched. Two things were wrong here.
    //
    // The select was unpaged over a table holding 1,431 error_log rows in the
    // last seven days alone, so it stopped at Supabase's 1000-row cap and
    // every one of these four figures was quietly short -- on the page whose
    // whole job is to tell you how bad things are.
    //
    // And `recent24h` filtered on created_at, which the select did not ask
    // for, so every comparison was against an Invalid Date and the array was
    // always empty. It was then never used. Removed rather than repaired:
    // a 24-hour figure nothing displays is not a feature.
    //
    // Counts are the right shape anyway. Four head:true queries move no rows
    // at all, and cannot be truncated by definition.
    const since7dISO = since7d.toISOString();
    const countOf = async (apply) => {
      let q = supabase.from("error_log").select("id", { count: "exact", head: true })
        .eq("company_id", companyId).gte("created_at", since7dISO);
      q = apply(q);
      const { count, error } = await q;
      if (error) { pmError("PM-8006", { raw: error, context: "error log stats", phase: "read", silent: true }); return 0; }
      return count || 0;
    };
    const [critical, error, reported, resolved] = await Promise.all([
      countOf(q => q.eq("severity", "critical").eq("resolved", false)),
      countOf(q => q.eq("severity", "error").eq("resolved", false)),
      countOf(q => q.eq("reported_by_user", true)),
      countOf(q => q.eq("resolved", true)),
    ]);
    setStats({ critical, error, reported, resolved });
    setLoading(false);
  }

  async function markResolved(id) {
    await supabase.from("error_log").update({ resolved: true, resolved_at: new Date().toISOString() }).eq("id", id);
    fetchErrors();
  }

  async function handleHealthCheck() {
    setRunningCheck(true);
    const v = await runDataIntegrityChecks(companyId, { deep: true });
    setViolations(v);
    setRunningCheck(false);
    showToast(v.length === 0 ? "Health check passed — no issues found" : `Health check found ${v.length} issue(s)`, v.length === 0 ? "success" : "warning");
  }

  const severityColor = { critical: "bg-danger-50 text-danger-700 border-danger-200", error: "bg-danger-50 text-danger-600 border-danger-200", warning: "bg-warning-50 text-warning-700 border-warning-200", info: "bg-info-50 text-info-700 border-info-200" };
  const severityDot = { critical: "text-danger-500", error: "text-danger-400", warning: "text-warning-500", info: "text-info-400" };
  const modules = ["auth", "properties", "tenants", "accounting", "banking", "payments", "work_orders", "infrastructure", "data_integrity"];

  return (
  <div>
  <div className="flex items-center justify-between mb-4">
  <PageHeader title="Error Log" />
  <Btn size="sm" onClick={handleHealthCheck} disabled={runningCheck}>{runningCheck ? "Running..." : "Run Health Check"}</Btn>
  </div>

  {/* Summary Cards */}
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
  <div className="bg-danger-50 border border-danger-200 rounded-xl p-3 text-center">
  <p className="text-2xl font-bold text-danger-700">{stats.critical}</p>
  <p className="text-xs text-danger-400">Critical (7d)</p>
  </div>
  <div className="bg-warning-50 border border-warning-200 rounded-xl p-3 text-center">
  <p className="text-2xl font-bold text-warning-700">{stats.error}</p>
  <p className="text-xs text-warning-400">Errors (7d)</p>
  </div>
  <div className="bg-brand-50 border border-brand-200 rounded-xl p-3 text-center">
  <p className="text-2xl font-bold text-brand-700">{stats.reported}</p>
  <p className="text-xs text-brand-400">User-Reported</p>
  </div>
  <div className="bg-positive-50 border border-positive-200 rounded-xl p-3 text-center">
  <p className="text-2xl font-bold text-positive-700">{stats.resolved}</p>
  <p className="text-xs text-positive-400">Resolved (7d)</p>
  </div>
  </div>

  {/* Health Check Violations */}
  {violations.length > 0 && (
  <div className="mb-4 p-3 bg-warning-50 border border-warning-200 rounded-xl">
  <div className="font-semibold text-warning-700 text-sm mb-2">Health Check Results ({violations.length} issues)</div>
  {violations.map((v, i) => (
  <div key={i} className="flex items-start gap-2 text-sm py-1">
  <span className="tnum text-xs bg-warning-100 text-warning-700 px-1.5 py-0.5 rounded font-bold shrink-0">{v.code}</span>
  <span className="text-neutral-600">{v.details}</span>
  </div>
  ))}
  </div>
  )}

  {/* Filters */}
  <div className="flex flex-wrap gap-2 mb-4 items-center">
  <Select value={filter.module} onChange={e => setFilter(f => ({ ...f, module: e.target.value }))} className="text-xs w-auto">
  <option value="">All Modules</option>
  {modules.map(m => <option key={m} value={m}>{m.replace("_", " ")}</option>)}
  </Select>
  <Select value={filter.severity} onChange={e => setFilter(f => ({ ...f, severity: e.target.value }))} className="text-xs w-auto">
  <option value="">All Severities</option>
  {["critical", "error", "warning", "info"].map(s => <option key={s} value={s}>{s}</option>)}
  </Select>
  <Select value={filter.range} onChange={e => setFilter(f => ({ ...f, range: e.target.value }))} className="text-xs w-auto">
  <option value="1d">Last 24h</option>
  <option value="7d">Last 7 days</option>
  <option value="30d">Last 30 days</option>
  <option value="">All time</option>
  </Select>
  <label className="flex items-center gap-1 text-xs text-neutral-500 cursor-pointer">
  <Checkbox checked={filter.reported} onChange={e => setFilter(f => ({ ...f, reported: e.target.checked }))} className="rounded" /> User-reported only
  </label>
  <label className="flex items-center gap-1 text-xs text-neutral-500 cursor-pointer">
  <Checkbox checked={filter.unresolved} onChange={e => setFilter(f => ({ ...f, unresolved: e.target.checked }))} className="rounded" /> Unresolved only
  </label>
  </div>

  {/* Error List */}
  {loading ? <p className="text-sm text-neutral-400">Loading...</p> : errors.length === 0 ? (
  <EmptyState icon="check-circle" message="No errors match your filters" />
  ) : (
  <div className="space-y-2">
  {errors.map(e => (
  <div key={e.id} className={"border rounded-xl p-3 " + (severityColor[e.severity] || "bg-subtle-50 border-subtle-200")}>
  <div className="flex items-start justify-between gap-2">
  <div className="flex-1 min-w-0">
  <div className="flex items-center gap-2 mb-1 flex-wrap">
  <span className={"text-xs tnum px-1.5 py-0.5 rounded font-bold " + (e.severity === "critical" || e.severity === "error" ? "bg-danger-100 text-danger-700" : "bg-warning-100 text-warning-700")}>{e.error_code}</span>
  <span className="text-xs text-neutral-400 capitalize">{e.severity}</span>
  <span className="text-xs text-neutral-300">{fmtDateTime(e.created_at)}</span>
  {e.user_email && <span className="text-xs text-neutral-400">{e.user_email}</span>}
  {e.reported_by_user && <span className="text-xs bg-brand-100 text-brand-700 px-1.5 py-0.5 rounded">Reported</span>}
  </div>
  <p className="text-sm font-medium text-neutral-700">{e.message}</p>
  {e.context && <p className="text-xs text-neutral-400 mt-0.5">Context: {e.context}</p>}
  {e.raw_message && e.raw_message !== e.message && <p className="text-xs text-neutral-300 mt-0.5 tnum truncate">Raw: {e.raw_message}</p>}
  </div>
  <div className="shrink-0">
  {!e.resolved && <Btn size="xs" variant="ghost" onClick={() => markResolved(e.id)}>Resolve</Btn>}
  {e.resolved && <span className="text-xs text-positive-600">Resolved</span>}
  </div>
  </div>
  </div>
  ))}
  </div>
  )}
  </div>
  );
}

// ============ COMPANY ============
// The company as it appears on paper -- name, address, phone, email and who
// signs for it -- in ONE place (Sahil, 2026-10-17: the same facts were asked
// for in three cards). Leases, notices, court forms, the lead disclosure
// and the rent payment page all read from here. The contact details live
// on the companies row; the signer is a company setting (settingsForm /
// updateSetting / saveSettings belong to the settings panel around this).
function CompanyContactCard({ companyId, showToast, userProfile, settingsForm, updateSetting, saveSettings, settingsDirty }) {
  const [row, setRow] = useState(null);
  const [form, setForm] = useState({ address: "", phone: "", email: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let live = true;
    supabase.from("companies").select("name, address, phone, email").eq("id", companyId).maybeSingle().then(({ data }) => {
      if (!live || !data) return;
      setRow(data); setForm({ address: data.address || "", phone: data.phone || "", email: data.email || "" });
    });
    return () => { live = false; };
  }, [companyId]);
  if (!row) return null;
  const contactDirty = form.address !== (row.address || "") || form.phone !== (row.phone || "") || form.email !== (row.email || "");
  const dirty = contactDirty || settingsDirty;
  const badEmail = form.email.trim() !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  async function save() {
    if (badEmail) { showToast("That email address does not look right.", "error"); return; }
    setSaving(true);
    try {
      if (contactDirty) {
        const next = { address: form.address.trim(), phone: form.phone.trim(), email: form.email.trim() };
        const { data, error } = await supabase.from("companies").update(next).eq("id", companyId).select("id");
        if (error || !(data || []).length) { pmError("PM-8006", { raw: error || new Error("no row updated"), context: "save company contact details" }); return; }
        setRow({ ...row, ...next }); setForm(next);
        logAudit("update", "settings", "Updated company contact details", "", userProfile?.email, "admin", companyId);
      }
      // The signer is a company setting: saved through the panel's own save
      // (fresh-read guard and all) when it changed.
      if (settingsDirty && saveSettings) await saveSettings({ quiet: true });
      showToast("Company details saved.", "success");
    } finally { setSaving(false); }
  }
  return (
  <div className="bg-white rounded-xl border border-neutral-200 p-4" data-testid="company-contact">
  <div className="text-xs font-semibold text-neutral-400 uppercase mb-1 flex items-center gap-1"><span className="material-icons-outlined text-sm">business</span>Company</div>
  <p className="text-xs text-neutral-400 mb-3">How <strong className="text-neutral-600">{row.name}</strong> appears on paper: leases, notices, court forms, the lead disclosure and the rent payment page all print these details, and the name below signs for the company.</p>
  <div className="grid grid-cols-3 gap-4">
  <div className="col-span-3">
  <SettingsField form={settingsForm || {}} update={updateSetting || (() => {})} label="Signs for the company" field="court_signer_name" type="text" />
  <span className="text-xs text-neutral-400">The landlord's printed name and default signer on every lease, move-in checklist and lead disclosure, and the signature line of every DC-CV-115, 082 and 081 (in a script font). Leave blank to use the sender's own name on leases and to sign court forms by hand.</span>
  </div>
  <div className="col-span-3">
  <label className="text-xs font-medium text-neutral-500 block mb-1">Address</label>
  <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Street, City, ST 12345" />
  <span className="text-xs text-neutral-400">On one line, ending with the city, state and ZIP: 100 Main St, Bowie, MD 20715</span>
  </div>
  <div><label className="text-xs font-medium text-neutral-500 block mb-1">Phone</label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
  <div className="col-span-2"><label className="text-xs font-medium text-neutral-500 block mb-1">Email</label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
  </div>
  <div className="mt-3"><Btn size="sm" onClick={save} disabled={!dirty || saving}>{saving ? "Saving…" : "Save company details"}</Btn></div>
  </div>
  );
}

// ============ ADMIN PAGE (Audit Trail + Team & Roles + Error Log) ============
// ============ COMPANY SETTINGS PANEL ============
// One labelled input of the Company Settings form. Module level, so its
// identity is stable across renders and the input keeps focus while typing.
function SettingsField({ form, update, label, field, type = "number", suffix, min, max, step }) {
  return (
    <div>
      <label className="text-xs font-medium text-neutral-500 block mb-1">{label}</label>
      <div className="flex items-center gap-1">
        <Input type={type} value={form[field] ?? ""} onChange={e => update(field, type === "number" ? Number(e.target.value) : e.target.value)} min={min} max={max} step={type === "number" ? (step || "1") : undefined} className="w-full" />
        {suffix && <span className="text-xs text-neutral-400 shrink-0">{suffix}</span>}
      </div>
    </div>
  );
}

function CompanySettingsPanel({ companyId, showToast, userProfile, companySettings, setCompanySettings }) {
  const [form, setForm] = useState({ ...companySettings });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  function update(key, value) {
    setForm(f => ({ ...f, [key]: value }));
    setDirty(true);
  }

  // Settings that failed to load show defaults; saving them would write the
  // defaults over the company's real settings (audit FE-18). Save is off
  // until they load, and is checked against a fresh read first.
  const loadFailed = !!companySettings?._loadError;
  async function retryLoad() {
    const fresh = await loadCompanySettings(companyId);
    if (fresh?._loadError) { showToast("Settings still could not be loaded. Try again in a moment.", "error"); return; }
    setCompanySettings(fresh); setForm({ ...fresh }); setDirty(false);
  }
  async function handleSave({ quiet = false } = {}) {
    setSaving(true);
    try {
      const fresh = await loadCompanySettings(companyId);
      if (fresh?._loadError) { showToast("Not saved: the current settings could not be read, so saving might overwrite them. Try again in a moment.", "error"); return; }
      const { _loadError, ...toSave } = form;
      await saveCompanySettings(companyId, toSave, userProfile?.email);
      setCompanySettings({ ...form });
      setDirty(false);
      if (!quiet) showToast("Settings saved!", "success");
      logAudit("update", "settings", "Updated company settings", "", userProfile?.email, "admin", companyId);
    } catch (e) {
      showToast("Error: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  }

  function resetDefaults() {
    setForm({ ...COMPANY_DEFAULTS });
    setDirty(true);
  }

  // SettingsField (module level) replaces a Field declared here: a
  // component declared inside the render is a new component every render,
  // so React remounted the input on each keystroke and the cursor vanished
  // (typing a name in Court Forms, 2026-10-06).

  return (
  <div className="space-y-6">
  {loadFailed && <div className="bg-danger-50 border border-danger-200 text-danger-800 rounded-xl px-4 py-3 text-sm flex items-center justify-between gap-3"><span>The company's settings could not be loaded, so the values below are defaults. Saving is off until they load.</span><Btn size="sm" variant="secondary" onClick={retryLoad}>Try again</Btn></div>}
  <div className="flex items-center justify-between">
  <div>
  <h3 className="text-sm font-bold text-neutral-700">Company Settings</h3>
  <p className="text-xs text-neutral-400">Configure defaults for this company. Changes apply to new entries only.</p>
  </div>
  <div className="flex items-center gap-2">
  <TextLink tone="neutral" size="xs" onClick={resetDefaults}>Reset to Defaults</TextLink>
  <Btn onClick={handleSave} disabled={saving || !dirty || loadFailed}>{saving ? "Saving..." : "Save Settings"}</Btn>
  </div>
  </div>

  <CompanyContactCard companyId={companyId} showToast={showToast} userProfile={userProfile} settingsForm={form} updateSetting={update} saveSettings={handleSave} settingsDirty={dirty && form.court_signer_name !== (companySettings?.court_signer_name ?? "")} />

  {/* Late Fees */}
  <div className="bg-white rounded-xl border border-neutral-200 p-4">
  <div className="text-xs font-semibold text-neutral-400 uppercase mb-3 flex items-center gap-1"><span className="material-icons-outlined text-sm">gavel</span>Late Fee Defaults</div>
  <div className="grid grid-cols-3 gap-4">
  <SettingsField form={form} update={update} label="Grace Period" field="late_fee_grace_days" suffix="days" min={0} max={30} />
  <SettingsField form={form} update={update} label="Fee Amount" field="late_fee_amount" suffix={form.late_fee_type === "percent" ? "%" : "$"} min={0} step="0.01" />
  <div>
  <label className="text-xs font-medium text-neutral-500 block mb-1">Fee Type</label>
  <Select value={form.late_fee_type} onChange={e => update("late_fee_type", e.target.value)}>
  <option value="flat">Flat ($)</option><option value="percent">Percent (%)</option>
  </Select>
  </div>
  </div>
  </div>

  {/* How tenants pay: printed as the rent payment information page of
      every first lease (leaseAttachments.js). Blank parts are left off. */}
  <div className="bg-white rounded-xl border border-neutral-200 p-4">
  <div className="text-xs font-semibold text-neutral-400 uppercase mb-3 flex items-center gap-1"><span className="material-icons-outlined text-sm">payments</span>Rent payment information (printed with a first lease)</div>
  {(() => {
    const pi = form.payment_info && typeof form.payment_info === "object" ? form.payment_info : {};
    const setPi = (k, v) => update("payment_info", { ...pi, [k]: v });
    // A render FUNCTION, not a component: a component defined here is a new
    // type on every keystroke, and React remounts the input (cursor lost).
    const field = ({ label, k, placeholder = "", type = "text", wide = false }) => (
      <div key={k} className={wide ? "col-span-2" : ""}>
        <label className="text-xs font-medium text-neutral-500 block mb-1">{label}</label>
        {type === "textarea"
          ? <Textarea rows={2} value={pi[k] ?? ""} onChange={e => setPi(k, e.target.value)} placeholder={placeholder} />
          : <Input type={type} value={pi[k] ?? ""} onChange={e => setPi(k, type === "number" ? Number(e.target.value) : e.target.value)} placeholder={placeholder} />}
      </div>
    );
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {field({ label: "Zelle email", k: "zelle_email", placeholder: "sigmahousingllc@gmail.com" })}
        {field({ label: "Bank", k: "bank_name", placeholder: "Bank of America" })}
        {field({ label: "Account number", k: "account_number" })}
        {field({ label: "Routing number", k: "routing_number" })}
        {field({ label: "Wire routing number", k: "wire_routing" })}
        {field({ label: "Rent due by (day)", k: "due_day", type: "number", placeholder: "1" })}
        {field({ label: "Late after (day)", k: "late_after_day", type: "number", placeholder: "5" })}
        <div className="col-span-2 md:col-span-4 text-xs text-neutral-400">The Zelle business name, the account holder and where checks are mailed are the company's name and address from the Company card above; the contact line is its email and phone.</div>
      </div>
    );
  })()}
  </div>

  {/* Lease Defaults */}
  <div className="bg-white rounded-xl border border-neutral-200 p-4">
  <div className="text-xs font-semibold text-neutral-400 uppercase mb-3 flex items-center gap-1"><span className="material-icons-outlined text-sm">description</span>Lease Defaults</div>
  <div className="grid grid-cols-3 gap-4">
  <SettingsField form={form} update={update} label="Default Lease Term" field="default_lease_months" suffix="months" min={1} max={60} />
  <SettingsField form={form} update={update} label="Security Deposit" field="default_deposit_months" suffix="month(s) rent" min={0} max={3} />
  <SettingsField form={form} update={update} label="Rent Escalation" field="rent_escalation_pct" suffix="% / year" min={0} max={25} step="0.5" />
  <SettingsField form={form} update={update} label="Payment Due Day" field="payment_due_day" suffix="of month" min={1} max={31} />
  <SettingsField form={form} update={update} label="Renewal Notice" field="renewal_notice_days" suffix="days" min={0} max={180} />
  <SettingsField form={form} update={update} label="Rent Increase Notice — lease term" field="rent_increase_notice_days" suffix="days (min 90)" min={90} max={180} />
  <SettingsField form={form} update={update} label="Rent Increase Notice — month to month" field="rent_increase_notice_days_mtm" suffix="days (min 60)" min={60} max={180} />
  </div>
  </div>

  {/* Notifications */}
  <div className="bg-white rounded-xl border border-neutral-200 p-4">
  <div className="text-xs font-semibold text-neutral-400 uppercase mb-3 flex items-center gap-1"><span className="material-icons-outlined text-sm">notifications</span>Notification Thresholds</div>
  <div className="grid grid-cols-3 gap-4">
  <SettingsField form={form} update={update} label="Rent Due Reminder" field="rent_due_reminder_days" suffix="days before" min={1} max={14} />
  <SettingsField form={form} update={update} label="Lease Expiry Warning" field="lease_expiry_warning_days" suffix="days before" min={7} max={180} />
  <SettingsField form={form} update={update} label="Insurance Expiry Warning" field="insurance_expiry_warning_days" suffix="days before" min={7} max={180} />
  <SettingsField form={form} update={update} label="Remind People Who Have Not Signed" field="auto_remind_signers_days" suffix="days (0 = never)" min={0} max={30} />
  </div>
  <label className="flex items-start gap-2 mt-4 text-sm text-neutral-700 cursor-pointer">
    <input type="checkbox" className="mt-0.5" checked={form.lease_clock_digest !== false} onChange={e => update("lease_clock_digest", e.target.checked)} />
    <span>Email the admins each morning when something new needs attention
      <span className="block text-xs text-neutral-400">A lease ending, a document nobody has signed, a move-out or a deposit statement coming due. Each thing is emailed once; the dashboard always shows the full list.</span>
    </span>
  </label>
  <p className="text-xs text-neutral-400 mt-2">Reminders to signers go out at most three times per person, and each one renews their signing link.</p>
  </div>

  {/* Legal */}
  <div className="bg-white rounded-xl border border-neutral-200 p-4">
  <div className="text-xs font-semibold text-neutral-400 uppercase mb-3 flex items-center gap-1"><span className="material-icons-outlined text-sm">balance</span>Legal & Lease Terms</div>
  <div className="grid grid-cols-3 gap-4">
  <SettingsField form={form} update={update} label="Deposit Return Deadline" field="deposit_return_days" suffix="days after move-out" min={1} max={90} />
  <SettingsField form={form} update={update} label="Termination Notice Required" field="termination_notice_days" suffix="days written" min={1} max={90} />
  <SettingsField form={form} update={update} label="Archive Retention" field="archive_retention_days" suffix="days" min={30} max={365} />
  </div>
  </div>

  {/* Other */}
  <div className="bg-white rounded-xl border border-neutral-200 p-4">
  <div className="text-xs font-semibold text-neutral-400 uppercase mb-3 flex items-center gap-1"><span className="material-icons-outlined text-sm">tune</span>Other Settings</div>
  <div className="grid grid-cols-3 gap-4">
  <SettingsField form={form} update={update} label="HOA Upcoming Window" field="hoa_upcoming_window_days" suffix="days" min={1} max={60} />
  <SettingsField form={form} update={update} label="Voucher Reexam Window" field="voucher_reexam_window_days" suffix="days" min={7} max={365} />
  </div>
  </div>
  </div>
  );
}

function AdminPage({ companyId, activeCompany, addNotification, userProfile, userRole, showToast, showConfirm, currentUser, companySettings, setCompanySettings }) {
  const [adminTab, setAdminTab] = useState("audit");
  const isAdmin = userRole === "admin";
  return (
  <div>
  <PageHeader title="Admin" />
  <p className="text-sm text-neutral-400 mb-4">Manage team access and view activity logs</p>
  {isAdmin && activeCompany?.company_code && (
  <div className="bg-brand-50/50 border border-brand-100 rounded-xl px-4 py-3 mb-4 flex items-center justify-between">
  <div>
  <div className="text-xs font-medium text-neutral-500">Company Join Code</div>
  <div className="text-lg font-bold tnum text-brand-700 tracking-wider">{activeCompany.company_code}</div>
  </div>
  <TextLink tone="brand" size="xs" underline={false} onClick={() => { navigator.clipboard.writeText(activeCompany.company_code); showToast("Code copied!", "success"); }} className="font-medium flex items-center gap-1">
  <span className="material-icons-outlined text-sm">content_copy</span>Copy
  </TextLink>
  </div>
  )}
  <div className="flex gap-1 mb-4 border-b border-brand-50 overflow-x-auto">
  <TabBar tabs={[["audit", "Audit Trail"], ...(isAdmin ? [["team", "Team & Roles"], ["notifications", "Notifications"], ["settings", "Settings"], ["errors", "Error Log"]] : [])]} active={adminTab} onChange={setAdminTab} size="md" />
  </div>
  {adminTab === "audit" && <AuditTrail companyId={companyId} />}
  {adminTab === "team" && isAdmin && <RoleManagement companyId={companyId} activeCompany={activeCompany} addNotification={addNotification} userProfile={userProfile} userRole={userRole} showToast={showToast} showConfirm={showConfirm} currentUser={currentUser} />}
  {adminTab === "notifications" && isAdmin && <AdminNotificationRules companyId={companyId} userProfile={userProfile} showToast={showToast} showConfirm={showConfirm} />}
  {adminTab === "settings" && isAdmin && <CompanySettingsPanel companyId={companyId} showToast={showToast} userProfile={userProfile} companySettings={companySettings} setCompanySettings={setCompanySettings} />}
  {adminTab === "errors" && isAdmin && <ErrorLogDashboard companyId={companyId} showToast={showToast} />}
  </div>
  );
}

// ============ AUDIT TRAIL ============
function AuditTrail({ companyId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterModule, setFilterModule] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  const [filterUser, setFilterUser] = useState("");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => { fetchLogs(); }, [companyId, page, filterModule, filterAction, filterUser]);

  async function fetchLogs() {
  setLoading(true);
  let query = supabase.from("audit_trail").select("*", { count: "exact" }).eq("company_id", companyId);
  if (filterModule !== "all") query = query.eq("module", filterModule);
  if (filterAction !== "all") query = query.eq("action", filterAction);
  if (filterUser) query = query.ilike("user_email", `%${escapeFilterValue(filterUser)}%`);
  const { data, count } = await query.order("created_at", { ascending: false }).range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
  setLogs(data || []);
  setTotalCount(count || 0);
  setLoading(false);
  }

  // Fetch distinct filter values once
  const [filterOptions, setFilterOptions] = useState({ modules: [], actions: [], users: [] });
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("audit_trail").select("module, action, user_email").eq("company_id", companyId).limit(1000);
      if (data) {
        setFilterOptions({
          modules: [...new Set(data.map(l => l.module))].sort(),
          actions: [...new Set(data.map(l => l.action))].sort(),
          users: [...new Set(data.map(l => l.user_email))].sort(),
        });
      }
    })();
  }, [companyId]);

  const modules = filterOptions.modules;
  const actions = filterOptions.actions;
  const users = filterOptions.users;

  const paged = logs;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const actionColors = {
  create: "bg-success-100 text-success-700",
  update: "bg-info-100 text-info-700",
  delete: "bg-danger-100 text-danger-700",
  request: "bg-warn-100 text-warn-700",
  approve: "bg-success-100 text-success-700",
  reject: "bg-danger-100 text-danger-700",
  };

  const moduleIcons = {
  properties: "🏠", tenants: "👤", payments: "💳", maintenance: "🔧",
  utilities: "⚡", accounting: "📊", documents: "📄", inspections: "🔍",
  autopay: "🔁",
  };

  if (loading) return <Spinner />;

  return (
  <div>
  <PageHeader title="Audit Trail" />
  <p className="text-sm text-neutral-400 mb-4">Complete activity log across all modules</p>

  {/* Filters */}
  <div className="flex flex-wrap gap-2 mb-4">
  <Select filter value={filterModule} onChange={e => { setFilterModule(e.target.value); setPage(0); }} >
  <option value="all">All Modules</option>
  {modules.map(m => <option key={m} value={m}>{moduleIcons[m] || "📌"} {m}</option>)}
  </Select>
  <Select filter value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(0); }} >
  <option value="all">All Actions</option>
  {actions.map(a => <option key={a} value={a}>{a}</option>)}
  </Select>
  <Input placeholder="Filter by user email..." value={filterUser} onChange={e => { setFilterUser(e.target.value); setPage(0); }} className="flex-1 min-w-48" />
  <Btn variant="slate" size="sm" onClick={fetchLogs}>Refresh</Btn>
  </div>

  {/* Stats */}
  <div className="grid grid-cols-4 gap-3 mb-4">
  <div className="bg-white rounded-xl border border-neutral-200 p-3 text-center">
  <p className="text-lg font-display font-bold text-neutral-800">{totalCount}</p>
  <p className="text-xs text-neutral-400">Total Actions</p>
  </div>
  <div className="bg-white rounded-xl border border-neutral-200 p-3 text-center">
  <p className="text-lg font-display font-bold text-neutral-800">{users.length}</p>
  <p className="text-xs text-neutral-400">Users Active</p>
  </div>
  <div className="bg-white rounded-xl border border-neutral-200 p-3 text-center">
  <p className="text-lg font-bold text-success-600">{logs.filter(l => l.action === "create").length}</p>
  <p className="text-xs text-neutral-400">Created</p>
  </div>
  <div className="bg-white rounded-xl border border-neutral-200 p-3 text-center">
  <p className="text-lg font-bold text-danger-500">{logs.filter(l => l.action === "delete").length}</p>
  <p className="text-xs text-neutral-400">Deleted</p>
  </div>
  </div>

  {/* Log Table */}
  <div className="bg-white rounded-xl border border-neutral-200 shadow-card overflow-hidden">
  <DataTable
    columns={[
      { key: "time", label: "Time", className: "text-xs text-neutral-400 whitespace-nowrap",
        render: log => (<>{fmtDateTime(log.created_at)}</>) },
      { key: "user", label: "User", className: "text-neutral-700 font-medium text-xs",
        render: log => (<>{log.user_email}</>) },
      { key: "role", label: "Role",
        render: log => (<>
          <span className={`text-xs px-1.5 py-0.5 rounded-full ${log.user_role === "admin" ? "bg-brand-100 text-brand-700" : "bg-neutral-100 text-neutral-500"}`}>{log.user_role}</span>
        </>) },
      { key: "module", label: "Module", className: "text-xs",
        render: log => (<>
          <span className="flex items-center gap-1">{moduleIcons[log.module] || "📌"} {log.module}</span>
        </>) },
      { key: "action", label: "Action",
        render: log => (<>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${actionColors[log.action] || "bg-neutral-100 text-neutral-700"}`}>{log.action}</span>
        </>) },
      { key: "details", label: "Details", className: "text-xs text-neutral-500 max-w-xs truncate",
        render: log => (<>{log.details}</>) },
    ]}
    rows={paged}
    rowKey={log => log.id}
    empty="Nothing to show"
  />
  </div>

  {/* Pagination */}
  {totalPages > 1 && (
  <div className="flex items-center justify-between mt-3">
  <span className="text-xs text-neutral-400">Page {page + 1} of {totalPages} ({totalCount} records)</span>
  <div className="flex gap-1">
  <Btn variant="slate" size="xs" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>← Prev</Btn>
  <Btn variant="slate" size="xs" onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>Next →</Btn>
  </div>
  </div>
  )}
  </div>
  );
}

// ============ USER PROFILE PAGE ============
function UserProfile({ currentUser, onBack, showToast, showConfirm }) {
  const [displayName, setDisplayName] = useState(currentUser?.user_metadata?.name || currentUser?.email?.split("@")[0] || "");
  const [phone, setPhone] = useState(currentUser?.user_metadata?.phone || "");
  const [avatarUrl, setAvatarUrl] = useState(currentUser?.user_metadata?.avatar_url || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem("theme") === "dark");

  async function saveProfile() {
  setSaving(true);
  const { error } = await supabase.auth.updateUser({ data: { name: displayName.trim(), phone: phone.trim(), avatar_url: avatarUrl } });
  if (error) pmError("PM-1009", { raw: error, context: "update user profile" });
  else showToast("Profile updated!", "success");
  setSaving(false);
  }

  async function sendPasswordReset() {
  const { error } = await supabase.auth.resetPasswordForEmail(currentUser?.email, { redirectTo: window.location.origin });
  if (error) pmError("PM-1004", { raw: error, context: "send password reset email" });
  else { setResetSent(true); showToast("Password reset link sent to " + currentUser?.email, "success"); }
  }

  async function uploadAvatar(e) {
  const file = e.target.files?.[0];
  if (!file) return;
  const ALLOWED_AVATAR_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
  if (!ALLOWED_AVATAR_TYPES.includes(file.type)) { showToast("Only image files allowed (JPG, PNG, GIF, WebP).", "error"); return; }
  // the shared check: real image bytes, not just the name (LIFE-15)
  const bad = await validateUploadFile(file, { kind: "image", maxMB: 2 });
  if (bad) { showToast(bad, "error"); return; }
  setUploading(true);
  const ext = file.type.split("/")[1] || "jpg";
  const path = `avatars/${currentUser.id}.${ext}`;
  const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert: true });
  if (error) { pmError("PM-7002", { raw: error, context: "upload avatar" }); setUploading(false); return; }
  const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);
  const publicUrl = urlData?.publicUrl + "?t=" + Date.now();
  setAvatarUrl(publicUrl);
  await supabase.auth.updateUser({ data: { avatar_url: publicUrl } });
  showToast("Avatar updated!", "success");
  setUploading(false);
  }

  async function deleteAccount() {
  if (deleteText !== "DELETE") return;
  setDeleting(true);
  // Phase 1: soft-delete memberships + flip app_users.status. These
  // are the user-visible effects and can be undone by an admin via DB.
  const { data: memberships } = await supabase.from("company_members").select("company_id").ilike("user_email", emailFilterValue(currentUser?.email));
  if (memberships) {
  for (const m of memberships) {
  await supabase.from("company_members").update({ status: "removed" }).eq("company_id", m.company_id).ilike("user_email", emailFilterValue(currentUser?.email));
  }
  }
  // app_users has archived_at/archived_by, not status/deleted_at. The
  // old payload was rejected outright, so "delete my account" never
  // marked the row at all — and the code moved straight on to deleting
  // the auth row, leaving an app_users record with no way to sign in
  // and no indication it had been closed.
  const { error: _selfDelErr } = await supabase.from("app_users")
    .update({ archived_at: new Date().toISOString(), archived_by: currentUser?.email || "" })
    .ilike("email", emailFilterValue(currentUser?.email));
  if (_selfDelErr) pmError("PM-1009", { raw: _selfDelErr, context: "self-delete: marking app_users archived" });

  // Phase 2: delete the Supabase auth row via server route. Until now
  // the auth row stayed alive — the user could still sign in, and any
  // UI path that didn't re-check app_users.status granted access.
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const tok = session?.access_token;
    if (tok) {
      const resp = await fetch("/api/account?action=self-delete", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + tok },
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        showToast("Account data cleared, but auth delete failed: " + (j.error || resp.status) + ". Contact support to finish.", "warning");
      }
    }
  } catch (_) { /* best effort; soft-delete already done */ }

  showToast("Account deleted. You will be signed out.", "info");
  setTimeout(async () => { await supabase.auth.signOut(); }, 1500);
  }

  function toggleDarkMode() {
  const next = !darkMode;
  setDarkMode(next);
  localStorage.setItem("theme", next ? "dark" : "light");
  document.documentElement.classList.toggle("dark", next);
  }

  return (
  <div className="min-h-dvh safe-y safe-x bg-gradient-to-br from-brand-50 to-white flex items-center justify-center p-4">
  <div className="w-full max-w-lg">
  <Btn variant="ghost" size="sm" onClick={onBack} className="mb-6">
  <span className="material-icons-outlined text-sm">arrow_back</span> Back to Companies
  </Btn>

  <div className="bg-white rounded-xl border border-neutral-200 shadow-card p-6 mb-4">
  <PageHeader title="Profile" />

  {/* Avatar */}
  <div className="flex items-center gap-4 mb-6">
  <div className="relative">
  {avatarUrl ? (
  <img src={avatarUrl} alt="Avatar" className="w-16 h-16 rounded-full object-cover border-2 border-brand-100" />
  ) : (
  <div className="w-16 h-16 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold text-2xl">
  {displayName?.[0]?.toUpperCase() || "U"}
  </div>
  )}
  <label className="absolute -bottom-1 -right-1 w-7 h-7 bg-brand-600 text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-brand-700 transition-colors">
  <span className="material-icons-outlined text-sm">photo_camera</span>
  <FileInput accept="image/*" onChange={uploadAvatar} className="hidden" />
  </label>
  </div>
  <div>
  <div className="font-semibold text-neutral-800">{displayName || "User"}</div>
  <div className="text-xs text-neutral-400">{currentUser?.email}</div>
  {uploading && <div className="text-xs text-brand-500 mt-1">Uploading...</div>}
  </div>
  </div>

  {/* Name & Phone */}
  <div className="space-y-3 mb-5">
  <div>
  <label className="text-xs font-medium text-neutral-500 block mb-1">Display Name</label>
  <Input value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Your name" />
  </div>
  <div>
  <label className="text-xs font-medium text-neutral-500 block mb-1">Phone Number</label>
  <Input value={phone} onChange={e => setPhone(formatPhoneInput(e.target.value))} placeholder="(555) 123-4567" maxLength={14} />
  </div>
  <div>
  <label className="text-xs font-medium text-neutral-500 block mb-1">Email</label>
  <Input value={currentUser?.email || ""} disabled className="bg-neutral-50 text-neutral-400" />
  </div>
  </div>

  <Btn size="lg" className="w-full mb-3" onClick={saveProfile} disabled={saving}>
  {saving ? "Saving..." : "Save Changes"}
  </Btn>
  </div>

  {/* Password Reset */}
  <div className="bg-white rounded-xl border border-neutral-200 shadow-card p-6 mb-4">
  <h3 className="font-semibold text-neutral-800 mb-2">Password</h3>
  <p className="text-xs text-neutral-400 mb-3">We'll send a password reset link to your email.</p>
  <Btn variant="slate" size="sm" onClick={sendPasswordReset} disabled={resetSent}>
  {resetSent ? "Reset Link Sent" : "Send Password Reset Email"}
  </Btn>
  </div>

  {/* Preferences */}
  <div className="bg-white rounded-xl border border-neutral-200 shadow-card p-6 mb-4">
  <h3 className="font-semibold text-neutral-800 mb-3">Preferences</h3>
  <div className="flex items-center justify-between py-2">
  <div>
  <div className="text-sm text-neutral-700">Dark Mode</div>
  <div className="text-xs text-neutral-400">Switch between light and dark theme</div>
  </div>
  <Switch checked={darkMode} onChange={toggleDarkMode} tone="brand" label="Dark mode" />
  </div>
  </div>

  {/* Authenticator app: the second sign-in step (2026-10-23). Staff only; the card
      hides itself for tenant and owner portal users. */}
  <MfaPanel currentUser={currentUser} showToast={showToast} showConfirm={showConfirm} />

  {/* Delete Account */}
  <div className="bg-white rounded-xl border border-danger-100 shadow-card p-6">
  <h3 className="font-semibold text-danger-600 mb-2">Delete Account</h3>
  <p className="text-xs text-neutral-400 mb-3">This will deactivate your account and remove you from all companies. This action cannot be undone.</p>
  {!showDeleteConfirm ? (
  <Btn variant="danger" size="sm" onClick={() => setShowDeleteConfirm(true)}>Delete My Account</Btn>
  ) : (
  <div className="space-y-3">
  <p className="text-sm text-danger-600 font-medium">Type "DELETE" to confirm:</p>
  <Input value={deleteText} onChange={e => setDeleteText(e.target.value.toUpperCase())} placeholder="Type DELETE" className="border-danger-200" />
  <div className="flex gap-2">
  <Btn variant="danger-fill" onClick={deleteAccount} disabled={deleteText !== "DELETE" || deleting}>{deleting ? "Deleting..." : "Permanently Delete"}</Btn>
  <Btn variant="slate" size="sm" onClick={() => { setShowDeleteConfirm(false); setDeleteText(""); }}>Cancel</Btn>
  </div>
  </div>
  )}
  </div>
  </div>
  </div>
  );
}

export { ArchivedItems, RoleManagement, ArchivePage, TasksAndApprovals, ErrorLogDashboard, AdminPage, AuditTrail, UserProfile, ROLES, ALL_NAV, ALL_NAV_FLAT, NAV_CHILD_IDS };
